import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    LinearProgress,
    Alert,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Snackbar,
    Tooltip,
    Grid,
    Divider,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    MenuList,
    ClickAwayListener,
    Popper
} from '@mui/material';
import {
    Check as CheckIcon,
    Close as CloseIcon,
    Help as HelpIcon,
    Warning as WarningIcon,
    Refresh as RefreshIcon,
    CheckCircle as CheckCircleIcon,
    Upload as UploadIcon,
    ExpandMore as ExpandMoreIcon,
    Error as ErrorIcon
} from '@mui/icons-material';
import EnclosureService from '../../../services/enclosureService';
import FileUploadComponent from '../../../components/FileUploadComponent';
import CalibrationlaboratoryService from '../../../services/calibrationlaboratoryService';
import {
    insertDANC,
    updateDANC,
    updateDANCBySourceId,
    getDANCByIdEnclosure
} from '../../../services/apiCalibration';
import { useParams, useLocation } from 'react-router-dom';
import { decryptText, decryptAppId } from '../../../utils/encryption';
import {
    getScrutinyRemarks
} from '../../../services/api';

// Utility function to get capacity from URL query parameters
const getCapacityFromQuery = (locationSearch) => {
    const searchParams = new URLSearchParams(locationSearch);
    const encryptedCapacity = searchParams.get("capacity");
    if (encryptedCapacity) {
        try {
            const decryptedCapacity = decryptText(encryptedCapacity);
            console.log(
                "📍 Using decrypted capacity from URL query:",
                decryptedCapacity
            );
            return decryptedCapacity;
        } catch (error) {
            return encryptedCapacity;
        }
    }
    return 0;
};

const AdequacyCheckSection = React.memo(({
    sectionId,
    title,
    applicationStage,
    orgId,
    appId,
    adequacyChecks,
    handleAdequacyCheck,
    handleAdequacyRemarks,
    handleAdequacySubmit,
    loading,
    currentUser = 'System User',
    currentUserId = 0
}) => {
    // Local state for DA NC operations
    const [dancLoading, setDancLoading] = useState(false);
    const [dancError, setDancError] = useState('');
    const [dancSuccess, setDancSuccess] = useState('');

    // Scrutiny remarks state
    const [scrutinyRemarks, setScrutinyRemarks] = useState([]);
    const [loadingRemarks, setLoadingRemarks] = useState(false);
    const [existingDANCRecord, setExistingDANCRecord] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [capacity, setCapacity] = useState(0);

    // Checkbox dropdown state
    const [selectedRemarksOptions, setSelectedRemarksOptions] = useState([]);
    const [isCheckboxDropdownOpen, setIsCheckboxDropdownOpen] = useState(false);

    // Load existing DA NC data on component mount
    useEffect(() => {
        const loadExistingDANCData = async () => {
            if (!orgId || !appId) return;

            try {
                setDancLoading(true);
                const userTypeId = localStorage.getItem('userTypeId');
                const expectedNCType = userTypeId === '4' ? 'Document Adequacy' : 'Assessment';
                console.log(`🔍 EnclosureSection - Loading DANC data for userType ${userTypeId} with expected NCtype: ${expectedNCType}`);

                const response = await getDANCByIdEnclosure(orgId, appId, title);
                console.log(`🔍 EnclosureSection - API Response:`, response);

                // Check if response is successful and has data array
                if (response && response.data && response.data.success && response.data.data && Array.isArray(response.data.data)) {
                    const allRecords = response.data.data;
                    console.log(`🔍 EnclosureSection - All DANC records:`, allRecords);

                    // Filter records by NCType for current user
                    const filteredRecords = allRecords.filter(record =>
                        record.NCType === expectedNCType
                    );

                    console.log(`🔍 EnclosureSection - Filtered records for NCType "${expectedNCType}":`, filteredRecords);

                    if (filteredRecords.length > 0) {
                        // Get the most recent record (highest updatedate or Insertdate)
                        const dancRecord = filteredRecords.sort((a, b) => {
                            const dateA = new Date(a.updatedate || a.Insertdate);
                            const dateB = new Date(b.updatedate || b.Insertdate);
                            return dateB - dateA; // Most recent first
                        })[0];

                        console.log(`🔍 EnclosureSection - Selected DANC record:`, dancRecord);
                        console.log(`🔍 EnclosureSection - DANC record matches userType ${userTypeId}, binding to dropdown...`);

                        setExistingDANCRecord(dancRecord);

                        // Bind DA NC data to adequacy check fields based on userTypeId
                        if (dancRecord.InformationStatus) {
                            console.log(`🔍 EnclosureSection - Binding InformationStatus: ${dancRecord.InformationStatus}`);
                            handleAdequacyCheck(sectionId, dancRecord.InformationStatus);
                        }

                        // Bind remarks to dropdown - prioritize based on userTypeId
                        let remarksToUse = '';
                        if (userTypeId === '4') {
                            // Document Adequacy user - prefer InformationProvided, fallback to Observation
                            remarksToUse = dancRecord.InformationProvided || dancRecord.Observation || '';
                        } else if (userTypeId === '6') {
                            // Assessment user - prefer Observation, fallback to InformationProvided
                            remarksToUse = dancRecord.Observation || dancRecord.InformationProvided || '';
                        }

                        if (remarksToUse) {
                            console.log(`🔍 EnclosureSection - Binding remarks for userType ${userTypeId}: ${remarksToUse}`);
                            handleAdequacyRemarks(sectionId, remarksToUse);
                        }

                        console.log(`🔍 EnclosureSection - Successfully bound DANC record to dropdown for userType ${userTypeId}`);
                    } else {
                        console.log(`🔍 EnclosureSection - No DANC records found for NCType "${expectedNCType}"`);
                        setExistingDANCRecord(null);
                    }
                } else {
                    console.log('🔍 EnclosureSection - No DANC records found or invalid response structure');
                    setExistingDANCRecord(null);
                }
                setDataLoaded(true);
            } catch (error) {
                console.log('🔍 EnclosureSection - No existing DA NC record found or error loading:', error.message);
                setExistingDANCRecord(null);
                setDataLoaded(true);
            } finally {
                setDancLoading(false);
            }
        };

        if (!dataLoaded) {
            loadExistingDANCData();
        }
    }, [
        orgId,
        appId,
        title,
        sectionId,
        dataLoaded,
        handleAdequacyCheck,
        handleAdequacyRemarks
    ]);

    // Fetch scrutiny remarks
    useEffect(() => {
        fetchScrutinyRemarks();
    }, [title]); // Add title dependency to refetch when title changes

    const fetchScrutinyRemarks = async () => {
        setLoadingRemarks(true);
        try {
            const responseApi = await getScrutinyRemarks({
                sectionName: title, // Use dynamic title instead of hardcoded value
                type: localStorage.getItem('userTypeId') === '6' ? 'Assessment' : 'Document Review'
            });
            const response = responseApi.data;
            if (response.success && response.data) {
                setScrutinyRemarks(response.data);
                console.log('Enclosure: Loaded scrutiny remarks:', response.data.length);
            } else {
                setScrutinyRemarks([]);
            }
        } catch (error) {
            console.error('Error fetching scrutiny remarks:', error);
            setScrutinyRemarks([]);
        } finally {
            setLoadingRemarks(false);
        }
    };
      // Function to get assessorId from URL query parameters with decryption
        // const getAssessorIdFromQuery = () => {
        //   const searchParams = new URLSearchParams(location.search);
        //   const encryptedAssessorId = searchParams.get("assessorId");
        //   if (encryptedAssessorId) {
        //     try {
        //       const decryptedAssessorId = decryptText(encryptedAssessorId);
        //       console.log(
        //         "📍 Using decrypted assessorId from URL query:",
        //         decryptedAssessorId
        //       );
        //       return decryptedAssessorId;
        //     } catch (error) {
        //       return encryptedAssessorId;
        //     }
        //   }
        //   return 0;
        // };

        // const assessorId = getAssessorIdFromQuery();

    // Clear messages after timeout
    useEffect(() => {
        if (dancError || dancSuccess) {
            const timer = setTimeout(() => {
                setDancError('');
                setDancSuccess('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [dancError, dancSuccess]);

    // Update selected options when adequacy remarks change
    useEffect(() => {
        const currentRemarks = adequacyChecks[sectionId]?.remarks || '';
        if (currentRemarks) {
            const remarksArray = currentRemarks.split(', ').map(remark => remark.trim()).filter(remark => remark !== '');
            setSelectedRemarksOptions(remarksArray);
        } else {
            setSelectedRemarksOptions([]);
        }
    }, [adequacyChecks, sectionId]);

    const userTypeId = localStorage.getItem('userTypeId');
    if ((applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") && (userTypeId !== '4' && userTypeId !== '6')) return null;
    if (applicationStage !== "18" && applicationStage !== "18a" && applicationStage !== "18b" && applicationStage !== "20") return null;

    // Check if adequacy checks should be read-only (stage 18a)
    const isAdequacyReadOnly = applicationStage === "18a";

    const adequacy = adequacyChecks[sectionId] || { status: '', remarks: '' };

    // Standard status change handler
    const handleStatusChange = (status) => {
        handleAdequacyCheck(sectionId, status);
    };

    // Standard remarks change handler
    const handleRemarksChange = (remarks) => {
        handleAdequacyRemarks(sectionId, remarks);
    };

    // Checkbox dropdown handlers
    const handleCheckboxChange = (remark, isChecked) => {
        let newSelectedOptions;
        if (isChecked) {
            newSelectedOptions = [...selectedRemarksOptions, remark];
        } else {
            newSelectedOptions = selectedRemarksOptions.filter(option => option !== remark);
        }
        setSelectedRemarksOptions(newSelectedOptions);
        
        // Update the remarks field with comma-separated values
        const commaSeparatedRemarks = newSelectedOptions.join(', ');
        handleAdequacyRemarks(sectionId, commaSeparatedRemarks);
    };

    const handleClearAllCheckboxes = () => {
        setSelectedRemarksOptions([]);
        handleAdequacyRemarks(sectionId, '');
    };

    // Enhanced submit handler with DA NC API call
    const handleSubmit = async () => {
        try {
            setDancLoading(true);
            setDancError('');
            setDancSuccess('');

            // First submit the adequacy check
            await handleAdequacySubmit(sectionId);
             const NCtype = userTypeId === '4' ? 'Document Adequacy' : 'Assessment';
            // Then create/update DA NC record based on status
            if (adequacy.status) {
                if (existingDANCRecord) {
                    // Update existing record
                    try {
                        const updateData = {
                            subsectionName: sectionId,
                            informationStatus: adequacy.status,
                            observation: adequacy.status === 'Inadequate'
                                ? (adequacy.remarks || 'Document adequacy marked as Inadequate')
                                : 'Document adequacy check completed',
                            isactive: 1,
                            NCtype: NCtype,
                            sourceid: 0
                        };

                        // Get assessorId and assessmentId (defaulting to 0 if not available)
                        const assessorId = localStorage.getItem('orgId') || 0;
                        const assessmentId = 0; // Default assessmentId for enclosure section

                        const response = await updateDANCBySourceId(orgId, appId, title, 0, assessmentId, assessorId, updateData);

                        if (response.success) {
                            setExistingDANCRecord(response.data);
                        } else {
                            throw new Error(response.message || 'Update failed');
                        }
                    } catch (updateError) {
                        console.error('DA-NC update failed:', updateError);
                    }
                } else {
                    // Create new record with userTypeId-based NCtype
                    try {
                        const userTypeId = localStorage.getItem('userTypeId');
                        const NCtype = userTypeId === '4' ? 'Document Adequacy' : 'Assessment';
                        console.log(`🔍 EnclosureSection - Creating DANC record for userType ${userTypeId} with NCtype: ${NCtype}`);

                        const dancData = {
                            Orgid: parseInt(orgId),
                            Appid: parseInt(appId),
                            SectionName: title,
                            SubsectionName: sectionId,
                            InformationProvided: adequacy.remarks || '',
                            InformationStatus: adequacy.status,
                            Observation: adequacy.status === 'Inadequate'
                                ? (adequacy.remarks || 'Document adequacy marked as Inadequate')
                                : 'Document adequacy check completed',
                            Insertedby: localStorage.getItem('orgId') || 0,
                            sourceid: 0,
                            NCtype: NCtype,
                        };

                        const response = await insertDANC(dancData);

                        if (response.data.success) {
                            setExistingDANCRecord(response.data.data);
                            setDancSuccess('Record created successfully');
                        } else {
                            throw new Error(response.message || 'Insert failed');
                        }
                    } catch (insertError) {
                        console.error('DA-NC creation failed:', insertError);
                        setDancError('Failed to create Record: ' + insertError.message);
                    }
                }
            }
        } catch (error) {
            console.error('Error submitting adequacy check:', error);
            setDancError('Error submitting adequacy check: ' + error.message);
        } finally {
            setDancLoading(false);
        }
    };

    return (
        <Box sx={{
            mb: 3,
            p: 3,
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            border: '2px solid #e9ecef',
            borderLeft: '4px solid #007bff'
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{
                    color: '#495057',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    📋 Adequacy Check - {title}
                    {isAdequacyReadOnly && (
                        <Chip
                            label="🔒 Read Only"
                            size="small"
                            sx={{
                                bgcolor: '#f5f5f5',
                                color: '#757575',
                                fontWeight: 'bold',
                                fontSize: '0.7rem'
                            }}
                        />
                    )}
                </Typography>

                {(adequacy.status || existingDANCRecord) && (
                    <Chip
                        label={`DA-NC: ${adequacy.status || existingDANCRecord?.InformationStatus || 'Active'}`}
                        size="small"
                        sx={{
                            bgcolor: (adequacy.status || existingDANCRecord?.InformationStatus) === 'Adequate' ? '#28a745' : '#dc3545',
                            color: 'white',
                            fontWeight: 'bold'
                        }}
                    />
                )}
            </Box>

            {/* DA NC Error/Success Messages */}
            {dancError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDancError('')}>
                    {dancError}
                </Alert>
            )}

            {dancSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDancSuccess('')}>
                    {dancSuccess}
                </Alert>
            )}

            <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            borderRadius: 2,
                            '&:hover': {
                                boxShadow: '0 2px 8px rgba(0,123,255,0.15)',
                            },
                            '&.Mui-focused': {
                                boxShadow: '0 4px 12px rgba(0,123,255,0.25)',
                            }
                        }
                    }}>
                        <InputLabel>Document Status</InputLabel>
                        <Select
                            value={adequacy.status}
                            onChange={isAdequacyReadOnly ? undefined : (e) => handleStatusChange(e.target.value)}
                            label="Document Status"
                            disabled={isAdequacyReadOnly}
                            sx={isAdequacyReadOnly ? {
                                backgroundColor: '#f5f5f5',
                                cursor: 'not-allowed',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e0e0e0',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e0e0e0',
                                }
                            } : undefined}
                        >
                            <MenuItem value="">-- Select Status --</MenuItem>
                            <MenuItem value="Adequate">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircleIcon sx={{ color: '#28a745', fontSize: '1rem' }} />
                                    Adequate
                                </Box>
                            </MenuItem>
                            <MenuItem value="Inadequate">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ErrorIcon sx={{ color: '#dc3545', fontSize: '1rem' }} />
                                    Inadequate
                                </Box>
                            </MenuItem>
                            {userTypeId === '4' && (
                            <MenuItem value="To be Verify">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ErrorIcon sx={{ color: '#dc3545', fontSize: '1rem' }} />
                                    To be Verify
                                </Box>
                            </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                </Grid>

                {adequacy.status === 'Inadequate' && (
                    <Grid item xs={12} md={8}>
                        {isAdequacyReadOnly ? (
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Remarks (View Only)"
                                placeholder="View Only (Read-only)"
                                value={adequacy.remarks || ''}
                                required={false}
                                variant="outlined"
                                InputProps={{
                                    readOnly: true,
                                    style: {
                                        backgroundColor: '#f5f5f5',
                                        cursor: 'not-allowed'
                                    }
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: 2,
                                        cursor: 'not-allowed',
                                    },
                                }}
                            />
                        ) : (
                            <Box sx={{ position: 'relative' }}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label={`Remarks (Required)${scrutinyRemarks.length > 0 ? ` - ${scrutinyRemarks.length} suggestions available` : ''}`}
                                    placeholder="Type your remarks here..."
                                    value={adequacy.remarks || ''}
                                    onChange={(e) => handleRemarksChange(e.target.value)}
                                    required
                                    variant="outlined"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: 'white',
                                            borderRadius: 2,
                                            '&:hover': {
                                                boxShadow: '0 2px 8px rgba(220,53,69,0.15)',
                                            },
                                            '&.Mui-focused': {
                                                boxShadow: '0 4px 12px rgba(220,53,69,0.25)',
                                            }
                                        }
                                    }}
                                />
                                
                                {/* Checkbox Dropdown */}
                                {scrutinyRemarks.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => setIsCheckboxDropdownOpen(!isCheckboxDropdownOpen)}
                                            sx={{
                                                textTransform: 'none',
                                                borderColor: '#1976d2',
                                                color: '#1976d2',
                                                '&:hover': {
                                                    borderColor: '#1565c0',
                                                    backgroundColor: '#e3f2fd'
                                                }
                                            }}
                                        >
                                            {isCheckboxDropdownOpen ? '🔽 Hide' : '🔽 Show'} Remarks Options ({scrutinyRemarks.length})
                                        </Button>
                                        
                                        {selectedRemarksOptions.length > 0 && (
                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={handleClearAllCheckboxes}
                                                sx={{ 
                                                    ml: 1, 
                                                    color: '#d32f2f',
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        backgroundColor: '#ffebee'
                                                    }
                                                }}
                                            >
                                                Clear All ({selectedRemarksOptions.length})
                                            </Button>
                                        )}
                                        
                                        {isCheckboxDropdownOpen && (
                                            <ClickAwayListener onClickAway={() => setIsCheckboxDropdownOpen(false)}>
                                                <Paper
                                                    elevation={3}
                                                    sx={{
                                                        mt: 1,
                                                        maxHeight: 300,
                                                        overflow: 'auto',
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: 2
                                                    }}
                                                >
                                                    <MenuList dense>
                                                        {scrutinyRemarks.map((remark, index) => {
                                                            const remarkText = remark.inadequate_remarks || '';
                                                            const isSelected = selectedRemarksOptions.includes(remarkText);
                                                            
                                                            return (
                                                                <MenuItem
                                                                    key={index}
                                                                    onClick={() => handleCheckboxChange(remarkText, !isSelected)}
                                                                    sx={{
                                                                        py: 1,
                                                                        px: 2,
                                                                        '&:hover': {
                                                                            backgroundColor: '#f5f5f5'
                                                                        }
                                                                    }}
                                                                >
                                                                    <Checkbox
                                                                        checked={isSelected}
                                                                        onChange={(e) => handleCheckboxChange(remarkText, e.target.checked)}
                                                                        size="small"
                                                                        color="primary"
                                                                        sx={{ mr: 1 }}
                                                                    />
                                                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                                                        {remarkText}
                                                                    </Typography>
                                                                </MenuItem>
                                                            );
                                                        })}
                                                    </MenuList>
                                                </Paper>
                                            </ClickAwayListener>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        )}
                        <Typography variant="caption" sx={{
                            color: '#6c757d',
                            display: 'block',
                            mt: 1,
                            fontStyle: 'italic'
                        }}>
                            {isAdequacyReadOnly
                                ? "Adequacy check is in read-only mode for stage 18a."
                                : (scrutinyRemarks.length > 0
                                    ? `${scrutinyRemarks.length} existing remark(s) available for "${title}". You can select from checkbox dropdown or type your own remarks.`
                                    : "Please specify what needs to be corrected or improved."
                                )
                            }
                        </Typography>
                    </Grid>
                )}
            </Grid>

            {adequacy.status && (
                <Box sx={{ mt: 2 }}>
                    <Alert
                        severity={existingDANCRecord
                            ? (adequacy.status === 'Adequate' ? 'info' : 'warning')
                            : (adequacy.status === 'Adequate' ? 'success' : 'warning')
                        }
                        sx={{
                            borderRadius: 2,
                            '& .MuiAlert-icon': {
                                fontSize: '1.2rem'
                            }
                        }}
                    >
                        {isAdequacyReadOnly
                            ? '🔒 Adequacy check is in read-only mode for stage 18a. No modifications can be made.'
                            : (existingDANCRecord
                                ? (adequacy.status === 'Adequate'
                                    ? `✓ Record exists. Click Update to modify.`
                                    : `⚠ Record exists. Click Update to modify.`)
                                : (adequacy.status === 'Adequate'
                                    ? '✓ This section is marked as Adequate. Click Submit to create Record.'
                                    : '⚠ Please address the remarks above and submit to create Record.'))}
                    </Alert>

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            size="medium"
                            onClick={handleSubmit}
                            disabled={
                                isAdequacyReadOnly ||
                                !adequacy.status ||
                                (adequacy.status === 'Inadequate' && !adequacy.remarks?.trim()) ||
                                loading ||
                                dancLoading
                            }
                            sx={{
                                background: existingDANCRecord
                                    ? 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)'
                                    : (adequacy.status === 'Adequate'
                                        ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
                                        : 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)'),
                                color: 'white',
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                                },
                                '&:disabled': {
                                    background: '#e9ecef',
                                    color: '#6c757d',
                                    transform: 'none',
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            {(loading || dancLoading) ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} sx={{ color: 'white' }} />
                                    {existingDANCRecord ? 'Updating...' : 'Submitting...'}
                                </Box>
                            ) : (
                                <>📤 {existingDANCRecord ? 'Update Adequacy Check' : 'Submit Adequacy Check'}</>
                            )}
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
});

const EnclosureSection = ({ data, onUpdate, allFormData = {} }) => {
    // Router hooks for encrypted parameters
    const { orgId: encryptedOrgId, appId: encryptedAppId } = useParams();
    const location = useLocation();
    // Extract encrypted parameters from URL query params or route params
    const getOrgId = useCallback(() => {
        try {
            // First try to get from URL query parameters
            const urlParams = new URLSearchParams(location.search);
            const orgIdParam = urlParams.get('orgId');
            if (orgIdParam) {
                return decryptText(orgIdParam);
            }

            // Then try to get from route parameters
            if (encryptedOrgId) {
                return decryptText(encryptedOrgId);
            }

            return null;
        } catch (error) {
            console.error('EnclosureSection - Error decrypting orgId:', error);
            return null;
        }
    }, [encryptedOrgId, location.search]);

    const getAppId = useCallback(() => {
        try {
            // First try to get from URL query parameters
            const urlParams = new URLSearchParams(location.search);
            const appIdParam = urlParams.get('appId');
            if (appIdParam) {
                return decryptAppId(appIdParam);
            }

            // Then try to get from route parameters
            if (encryptedAppId) {
                return decryptAppId(encryptedAppId);
            }

            return null;
        } catch (error) {
            console.error('EnclosureSection - Error decrypting appId:', error);
            return null;
        }
    }, [encryptedAppId, location.search]);

    // Get capacity from URL query parameters
    const capacity = getCapacityFromQuery(location.search);

    // State management - Updated to fix getChecklistStatus error
    const [checklistData, setChecklistData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'info'
    });

    // Application state
    const [applicationStage, setapplicationStage] = useState(null);
    const [isFreezed, setIsFreezed] = useState(false);
    const [labTypeId, setLabTypeId] = useState(2);
    const [standard, setStandard] = useState(2017);

    // Declaration states
    const [showDeclaration, setShowDeclaration] = useState(false);
    const [declarationData, setDeclarationData] = useState({
        orgDeclaration: false,
        orgName: '',
        adverseDeclaration: false,
        adverseDetails: ''
    });

    // Dialog states
    const [submitDialog, setSubmitDialog] = useState(false);
    const [sendToLADialog, setSendToLADialog] = useState(false);
    const [password, setPassword] = useState('');

    // Proficiency Testing Document – discipline wise file upload
    const [proficiencyFiles, setProficiencyFiles] = useState({});
    const [disciplines, setDisciplines] = useState([]);
    const [disciplinesLoading, setDisciplinesLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Adequacy Check States
    const [adequacyChecks, setAdequacyChecks] = useState({});
    const [adequacyLoading, setAdequacyLoading] = useState(false);

    // Adequacy Check Handlers
    const handleAdequacyCheck = (sectionId, status) => {
        setAdequacyChecks(prev => ({
            ...prev,
            [sectionId]: {
                ...(prev[sectionId] || {}),
                status: status
            }
        }));
    };

    const handleAdequacyRemarks = (sectionId, remarks) => {
        setAdequacyChecks(prev => ({
            ...prev,
            [sectionId]: {
                ...(prev[sectionId] || {}),
                remarks: remarks
            }
        }));
    };

    const handleAdequacySubmit = async (sectionId) => {
        setAdequacyLoading(true);
        return new Promise((resolve) => {
            setTimeout(() => {
                setAdequacyChecks(prev => ({
                    ...prev,
                    [sectionId]: {
                        ...prev[sectionId],
                        isSubmitted: true,
                        submittedAt: new Date().toISOString()
                    }
                }));
                setAdequacyLoading(false);
                resolve();
            }, 500);
        });
    };

    // Load adequacy check data from API
    const loadAdequacyCheckData = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await CalibrationlaboratoryService.getLaboratoryAdequacyCheck(getAppId(), getOrgId(), labTypeId);

            if (response.success && response.data) {
                // Transform API response to match our component structure
                const transformedData = response.data.map((item, index) => ({
                    id: index + 1,
                    sno: index + 1,
                    information: item.line,
                    status: item.chec === '~/assets/pngegg 2.svg' ? 'completed' : 'not-filled', // Use chec field: pngegg 2 = completed, pngegg 1 = not-filled
                    chec: item.chec, // Use the chec field directly from API response
                    show: item.show,
                    remarks: item.Remarks,
                    link: item.link,
                    tooltip: item.Remarks ? item.Remarks.replace(/<br \/>/g, ' ') : `Complete this section: ${item.line}`
                }));

                 setChecklistData(transformedData);

                // Check completion status
                const allComplete = transformedData.every(item => item.status === 'completed');
                setShowDeclaration(allComplete);

                // Update localStorage to inform layout about completion
                localStorage.setItem('medicalEnclosureCompleted', allComplete ? 'true' : 'false');

                // Trigger a custom event to immediately update the layout
                window.dispatchEvent(new CustomEvent('enclosureStatusChanged', {
                    detail: { completed: allComplete }
                }));

            } else {
                throw new Error(response.message || 'Invalid API response format');
            }

        } catch (error) {
            console.error('Error loading adequacy check data:', error);
            setError(error.message || 'Failed to load adequacy check data');
            showNotification('Error loading checklist data. Using default validation.', 'warning');

        } finally {
            setLoading(false);
        }
    };

    // Fetch disciplines for proficiency testing
    const fetchDisciplines = useCallback(async () => {
        setDisciplinesLoading(true);
        try {
            const response = await CalibrationlaboratoryService.getSelectedDisciplinesForStaff(
                getAppId(),
                getOrgId()
            );
            if (response.success) {
                setDisciplines(response.data);
                console.log('Fetched disciplines for PT:', response.data);
            } else {
                showNotification('Failed to fetch disciplines', 'error');
            }
        } catch (error) {
            console.error('Error fetching disciplines:', error);
            showNotification('Error fetching disciplines: ' + error.message, 'error');
        } finally {
            setDisciplinesLoading(false);
        }
    }, [getAppId, getOrgId]);

    // Load existing proficiency files from laboratory details
   const loadProficiencyFiles = useCallback(async () => {
     try {
       const response = await CalibrationlaboratoryService.getQMSFile(getAppId(),getOrgId());
       const QMSFile = response?.data?.QMSFile;
       if (QMSFile) {
         const files = JSON.parse(QMSFile);
         const filesMap = {};
         files.forEach((file) => {
           const key = `${file.disciplineId}_${file.groupId}`;
           filesMap[key] = {
             ...file,
             storage: "S3",
           };
         });
         setProficiencyFiles(filesMap);
       }
     } catch (error) {
       console.error("Error loading proficiency files:", error);
     }
   }, [getAppId, getOrgId]);

    // Save all proficiency files to database
    const handleSaveProficiencyFiles = async () => {
      if (Object.keys(proficiencyFiles).length === 0) {
        showNotification(
          "No files to save. Please upload at least one document.",
          "warning",
        );
        return;
      }
      const missingFiles = disciplines.filter((discipline) => {
        const disciplineId = discipline.DisciplineId || discipline.Id;
        const groupId = discipline.GroupId || discipline.groupId;
        const key = `${disciplineId}_${groupId}`;
        return !proficiencyFiles[key];
      });
      if (missingFiles.length > 0) {
        showNotification("Please upload QMS document for all Disciplines/Groups.","warning");
        return;
      }
      setSaving(true);
      try {
        // Build array of file references using the disciplines list to get groupId
        const filesToSave = disciplines
          .filter((discipline) => {
            const disciplineId = discipline.DisciplineId || discipline.Id;
            const groupId = discipline.GroupId || discipline.groupId;
            const key = `${disciplineId}_${groupId}`;
            return proficiencyFiles[key] !== undefined;
          })
          .map((discipline) => {
            const disciplineId = discipline.DisciplineId || discipline.Id;
            const groupId = discipline.GroupId || discipline.groupId;
            const key = `${disciplineId}_${groupId}`;
            const fileData = proficiencyFiles[key];
            return {
              disciplineId: parseInt(disciplineId),
              groupId: parseInt(groupId),
              fileName: fileData.fileName || fileData.originalName || "",
              key: fileData.key || "",
              originalName: fileData.originalName || fileData.fileName || "",
              uploadDate: fileData.uploadDate || new Date().toISOString(),
            };
          });

        if (filesToSave.length === 0) {
          showNotification("No files to save.", "warning");
          setSaving(false);
          return;
        }
        // Call API to update laboratory proficiency docs
        const result = await CalibrationlaboratoryService.updateQMSFile(
          getAppId(),
          getOrgId(),
          filesToSave,
        );
        if (result.success) {
          showNotification(
            "Proficiency documents saved successfully",
            "success",
          );
        } else {
          showNotification(
            result.message || "Failed to save documents",
            "error",
          );
        }
      } catch (error) {
        console.error("Error saving proficiency files:", error);
        showNotification(
          "Error saving proficiency files: " + error.message,
          "error",
        );
      } finally {
        setSaving(false);
      }
    };

    // Handle file upload for a specific discipline
    const handleProficiencyFileChange = (disciplineId, groupId, fieldName, fileData) => {
        const key = `${disciplineId}_${groupId}`;
        setProficiencyFiles(prev => ({
            ...prev,
            [key]: fileData
        }));
    };

    // Fallback validation functions (same as before)
    const validateLegalEntityDocuments = () => {
        const labData = allFormData.laboratoryDetails || {};
        const orgData = allFormData.organization || {};

        if (!labData.legalEntityType) return 'not-filled';

        const hasEntityDocument =
            labData.proprietorshipCertificateFile ||
            labData.partnershipDeedFile ||
            labData.llpCertificateFile ||
            labData.companiesActCertificateFile ||
            labData.publicLtdCertificateFile ||
            labData.governmentCertificateFile ||
            labData.trustCertificateFile ||
            labData.societyCertificateFile ||
            orgData.managementSystemDocument;

        return hasEntityDocument ? 'completed' : 'not-filled';
    };

    const validateScopeAndTestMethods = () => {
        const scopeData = allFormData.scope || {};
        const hasScope = scopeData.scopes && scopeData.scopes.length > 0;
        const hasMethods = scopeData.testMethods && scopeData.testMethods.length > 0;

        if (hasScope && hasMethods) return 'completed';
        if (hasScope || hasMethods) return 'partial';
        return 'not-filled';
    };

    const validateSeniorManagementDetails = () => {
        const personnelData = allFormData.personnel || {};
        const labStaffData = allFormData.labStaff || {};

        const hasManagement =
            (personnelData.seniorManagement && personnelData.seniorManagement.length > 0) ||
            (labStaffData.staff && labStaffData.staff.some(staff =>
                staff.designation && staff.designation.toLowerCase().includes('manager')
            ));

        return hasManagement ? 'completed' : 'not-filled';
    };

    const validateStaffDetails = () => {
        const labStaffData = allFormData.labStaff || {};
        const personnelData = allFormData.personnel || {};

        const hasStaff =
            (labStaffData.staff && labStaffData.staff.length > 0) ||
            (personnelData.staff && personnelData.staff.length > 0);

        const hasAuthorizationDetails = labStaffData.staff &&
            labStaffData.staff.some(staff => staff.reportReview === 'Yes');

        if (hasStaff && hasAuthorizationDetails) return 'completed';
        if (hasStaff) return 'partial';
        return 'not-filled';
    };

    const validateOrganizationChart = () => {
        const orgData = allFormData.organization || {};
        const labData = allFormData.laboratoryDetails || {};

        const hasOrgChart =
            orgData.organizationChart ||
            orgData.parentOrganizationChart ||
            labData.organizationChartFile;

        return hasOrgChart ? 'completed' : 'not-filled';
    };

    const validateEquipmentList = () => {
        const equipmentData = allFormData.equipment || {};
        const hasEquipment = equipmentData.equipmentList && equipmentData.equipmentList.length > 0;
        const hasTraceability = equipmentData.equipmentList &&
            equipmentData.equipmentList.some(eq => eq.traceability || eq.calibrationCertificate);

        if (hasEquipment && hasTraceability) return 'completed';
        if (hasEquipment) return 'partial';
        return 'not-filled';
    };

    const validateQualityControlActivity = () => {
        const ptData = allFormData.proficiencyTesting || {};
        const labData = allFormData.laboratoryDetails || {};

        const hasPTParticipation = ptData.programmes && ptData.programmes.length > 0;
        const hasQCActivity =
            labData.qualityControlProgram ||
            labData.internalQualityControl ||
            hasPTParticipation;

        return hasQCActivity ? 'completed' : 'not-filled';
    };

    const validateInternalAuditManagementReview = () => {
        const labData = allFormData.laboratoryDetails || {};

        const hasInternalAudit =
            labData.internalAuditFromDate && labData.internalAuditToDate;
        const hasManagementReview =
            labData.managementReviewFromDate && labData.managementReviewToDate;
        const hasAuditDocument = labData.internalAuditDocument;
        const hasReviewDocument = labData.managementReviewDocument;

        if (hasInternalAudit && hasManagementReview && hasAuditDocument && hasReviewDocument) {
            return 'completed';
        }
        if (hasInternalAudit || hasManagementReview) return 'partial';
        return 'not-filled';
    };

    const validateManagementSystemDocument = () => {
        const orgData = allFormData.organization || {};
        const labData = allFormData.laboratoryDetails || {};

        const hasQualityManual =
            orgData.managementSystemDocument ||
            labData.qualityManualFile ||
            labData.managementSystemFile;

        return hasQualityManual ? 'completed' : 'not-filled';
    };

    // Initialize data
    useEffect(() => {
        // Load adequacy check data from API first
        loadAdequacyCheckData();

        // Determine application stage and permissions
        determineApplicationStage();

        // Fetch disciplines and load existing files
        fetchDisciplines();
        loadProficiencyFiles();

        // Load previously uploaded file if available (legacy)
        if (data && data.uploadedFile) {
            // We keep this for backward compatibility, but we now use discipline-wise
        }

        // Load existing QMS file from database (if needed)
        // loadQMSFile();
    }, []);

    // Load existing QMS file from laboratory API
    const loadQMSFile = async () => {
        try {
            const response = await CalibrationlaboratoryService.getLaboratoryDetails(getOrgId());
            console.log('Laboratory Details API Response:', response);
            console.log('Full lab data:', response?.data);

            // Extract QMS file from laboratory details - check all possible field names
            const labData = response?.data;


            const qmsFileName = labData?.QMSFile || labData?.QMSFile || labData?.QMSFile || labData?.QMSFile;

            if (qmsFileName && qmsFileName !== '') {
                console.log('Found QMS file in laboratory details:', qmsFileName);

                // Create file data object with the filename
                // The key should be 'live/{filename}' to match S3 storage location
                const fileData = {
                    fileName: qmsFileName,
                    key: `live/${qmsFileName}`,  // Files are stored in live/ folder directly
                    originalName: qmsFileName,
                    storage: 'S3',
                    uploadDate: labData?.lastUpdated || labData?.updatedAt || new Date().toISOString()
                };

                // setUploadedFile(fileData); // commented as this is legacy
                console.log('QMS file loaded and set:', fileData);
            } else {
                console.log('No QMS file found in laboratory details');
                console.log('Searched for fields: QMS, qms, qmsFile, Qms');
            }
        } catch (error) {
            console.error('Error loading QMS file from laboratory details:', error);
            // Don't show error notification, file might not exist yet
        }
    };

    const determineApplicationStage = async () => {
        try {
            // Fetch application stage from API
            const appId = getAppId();
            if (appId) {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/laboratory/application-stage/${appId}`);
                const result = await response.json();

                if (result.success && result.stage) {
                    setapplicationStage(result.stage);
                    console.log('Application stage loaded:', result.stage);
                } else {
                    setapplicationStage(null); // Stage not found, set to null
                    console.log('Stage not found, setting to null');
                }
            } else {
                setapplicationStage(null); // No appId, set to null
            }

            setIsFreezed(false); // Default to not frozen

            // Show declaration if all checklist items are complete
            const stats = getCompletionStats();
            const allComplete = stats.pending === 0 && stats.total > 0;
            setShowDeclaration(allComplete);

            // Update localStorage to inform layout about completion
            localStorage.setItem('medicalEnclosureCompleted', allComplete ? 'true' : 'false');

            // Trigger a custom event to immediately update the layout
            window.dispatchEvent(new CustomEvent('enclosureStatusChanged', {
                detail: { completed: allComplete }
            }));

        } catch (error) {
            console.error('Error determining application stage:', error);
            // Use null as default if API fails
            setapplicationStage(null);
            setIsFreezed(false);
        }
    };

    // Helper functions for UI
    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return <CheckIcon fontSize="medium" sx={{ color: 'success.main' }} />;
            case 'partial':
                return <WarningIcon fontSize="medium" sx={{ color: 'warning.main' }} />;
            case 'not-applicable':
                return <HelpIcon fontSize="medium" sx={{ color: 'info.main' }} />;
            case 'not-filled':
            default:
                return <CloseIcon fontSize="medium" sx={{ color: 'error.main' }} />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'partial':
                return 'warning';
            case 'not-applicable':
                return 'info';
            case 'not-filled':
            default:
                return 'error';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'completed':
                return 'Complete';
            case 'partial':
                return 'Partial';
            case 'not-applicable':
                return 'N/A';
            case 'not-filled':
            default:
                return 'Pending';
        }
    };

    // Calculate overall completion percentage
    const getCompletionPercentage = () => {
        const applicableItems = checklistData.filter(item => item.status !== 'not-applicable');
        const completedItems = applicableItems.filter(item => item.status === 'completed');
        const partialItems = applicableItems.filter(item => item.status === 'partial');

        if (applicableItems.length === 0) return 100;

        const score = (completedItems.length * 1) + (partialItems.length * 0.5);
        return Math.round((score / applicableItems.length) * 100);
    };

    // Show notification
    const showNotification = (message, severity = 'info') => {
        setNotification({
            open: true,
            message,
            severity
        });
    };

    // Close notification
    const closeNotification = () => {
        setNotification(prev => ({ ...prev, open: false }));
    };

    // Handle file upload (legacy, kept for compatibility)
    const handleFileUpload = (fieldName, uploadData) => {
        console.log(`File uploaded for ${fieldName}:`, uploadData);

        if (uploadData && uploadData.key) {
            const fileData = {
                fileName: uploadData.fileName || uploadData.originalName,
                key: uploadData.key,
                originalName: uploadData.originalName,
                fileSize: uploadData.fileSize,
                uploadDate: uploadData.uploadDate || new Date().toISOString(),
                storage: 'S3'
            };

            // setUploadedFile(fileData); // legacy

            // Notify parent component if needed
            if (onUpdate) {
                onUpdate({
                    ...data,
                    uploadedFile: fileData
                });
            }

            showNotification('File uploaded successfully to S3. Click "Save File" to save permanently.', 'success');
        } else if (uploadData === null) {
            // File was deleted
            // setUploadedFile(null); // legacy

            if (onUpdate) {
                onUpdate({
                    ...data,
                    uploadedFile: null
                });
            }

            showNotification('File removed successfully', 'info');
        }
    };

    // Save file name to database (QMS column) – kept for legacy
    const handleSaveToDatabase = async () => {
        // This is kept for backward compatibility; the new method is handleSaveProficiencyFiles
        showNotification('Please use the "Save All Proficiency Documents" button for the new discipline-wise upload.', 'info');
    };

    // Application submission handlers
    const handlePreviewReport = () => {
        showNotification('Opening preview report...', 'info');
        // Navigate to preview report page
        window.open('/medical/preview-report', '_blank');
    };

    const handleSubmitApplication = async () => {
        try {
            // Check if Management System Document is uploaded in organization section
            const orgData = allFormData.organization || {};
            if (!orgData.managementSystemDocument) {
                showNotification('Please upload Management System Document in Organization section for final submission', 'error');
                return;
            }

            // Prepare submission data
            const submissionData = {
                orgId: getOrgId(),
                appId: getAppId(),
                orgDeclaration: declarationData.orgDeclaration,
                declNameOrg: declarationData.orgName,
                adverseDeclaration: declarationData.adverseDeclaration,
                declAdverseDetails: declarationData.adverseDetails
            };

            await EnclosureService.submitApplication(submissionData.orgId, submissionData.appId, submissionData);

            showNotification('Application submitted successfully', 'success');
            setSubmitDialog(false);

            // Update application stage
            setapplicationStage('3a');

        } catch (error) {
            console.error('Error submitting application:', error);
            showNotification('Error submitting application. Please try again.', 'error');
        }
    };

    const handleSendToLA = async () => {
        try {
            if (!password) {
                showNotification('Please enter your password', 'error');
                return;
            }

            await EnclosureService.sendToCAB(getAppId(), password);

            showNotification('Application sent to CAB successfully', 'success');
            setSendToLADialog(false);
            setPassword('');

            // Navigate to CAB section
            // TODO: Implement navigation
            // window.location.href = '/cabNew/default';

        } catch (error) {
            console.error('Error sending to LA:', error);
            showNotification('Invalid password or error occurred', 'error');
        }
    };

    // Get completion statistics
    const getCompletionStats = () => {
        const total = checklistData.length;
        const completed = checklistData.filter(item => item.status === 'completed').length;
        const partial = checklistData.filter(item => item.status === 'partial').length;
        const pending = checklistData.filter(item => item.status === 'not-filled').length;
        const notApplicable = checklistData.filter(item => item.status === 'not-applicable').length;

        return { total, completed, partial, pending, notApplicable };
    };

    // Refresh data function
    const handleRefresh = () => {
        loadAdequacyCheckData();
    };

    return (
        <Box sx={{ p: 2 }}>
            <Paper elevation={3} sx={{ p: 2 }}>
                {/* Proficiency Testing Document – Discipline wise upload */}
                <Box>
                    <Typography variant="h6" gutterBottom>
                        Proficiency Testing Document
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Upload proficiency test reports for each discipline/group.
                    </Typography>

                    {disciplinesLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : disciplines.length === 0 ? (
                        <Alert severity="info">
                            No disciplines found. Please add disciplines in the discipline section first.
                        </Alert>
                    ) : (
                        <Grid container spacing={1}>
                            {disciplines.map((discipline) => {
                                const disciplineId = discipline.DisciplineId || discipline.Id;
                                const groupId = discipline.GroupId || discipline.groupId;
                                const key = `${disciplineId}_${groupId}`;
                                const fileData = proficiencyFiles[key] || null;
                                return (
                                    <Grid item xs={12} md={6} key={key}>
                                        <Paper
                                            elevation={1}
                                            sx={{
                                                p: 1,
                                                border: "1px solid #e0e0e0",
                                                borderRadius: 2,
                                                backgroundColor: "#fafafa",
                                                transition: "all 0.2s",
                                                "&:hover": {
                                                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                                    borderColor: "#1976d2",
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    mb: 1,
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle1"
                                                    fontWeight="bold"
                                                >
                                                    {discipline.DisciplineName}
                                                </Typography>
                                                <Chip
                                                    label={discipline.groupName || discipline.GroupName}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{
                                                        ml: 0.5,
                                                        height: "auto",
                                                        "& .MuiChip-label": {
                                                            display: "block",
                                                            whiteSpace: "normal",
                                                            wordBreak: "break-word",
                                                            padding: "5px",
                                                        },
                                                    }}
                                                />
                                            </Box>
                                            <FileUploadComponent
                                                fieldName={`proficiency_${key}`}
                                                label="Upload Document"
                                                orgId={getOrgId()}
                                                section="enclosure"
                                                required={false}
                                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.xls"
                                                maxSize={10}
                                                compact={true}
                                                onChange={(fieldName, fileData) =>
                                                    handleProficiencyFileChange(
                                                        disciplineId,
                                                        groupId,
                                                        fieldName,
                                                        fileData
                                                    )
                                                }
                                                initialFile={fileData}
                                                localDeleteOnly={true}
                                            />
                                        </Paper>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}

                    {/* Save Button – only when editable */}
                    {(applicationStage === null || applicationStage === '18a') && (
                        <Box sx={{ mt: 3 }}>
                            <Button
                                variant="contained"
                                onClick={handleSaveProficiencyFiles}
                                disabled={Object.keys(proficiencyFiles).length === 0 || saving}
                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                                sx={{
                                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                                    color: 'white',
                                    boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                                    '&:hover': {
                                        background: 'linear-gradient(45deg, #1976D2 30%, #00BCD4 90%)',
                                    },
                                    '&:disabled': {
                                        background: '#cccccc',
                                        color: '#666666'
                                    }
                                }}
                            >
                                {saving ? 'Saving...' : 'Save All Proficiency Documents'}
                            </Button>
                        </Box>
                    )}
                </Box>

                {/* Adequacy Check Section – unchanged */}
                {(applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") && (
                    <Box sx={{ mt: 4, mb: 4 }}>
                        <AdequacyCheckSection
                            sectionId="enclosure"
                            title="Proficiency Testing Document"
                            applicationStage={applicationStage}
                            orgId={getOrgId()}
                            appId={getAppId()}
                            adequacyChecks={adequacyChecks}
                            handleAdequacyCheck={handleAdequacyCheck}
                            handleAdequacyRemarks={handleAdequacyRemarks}
                            handleAdequacySubmit={handleAdequacySubmit}
                            loading={adequacyLoading}
                            currentUser={localStorage.getItem('userName') || 'System User'}
                            currentUserId={parseInt(localStorage.getItem('userId') || '0')}
                        />
                    </Box>
                )}

                {/* Existing checklist table – shown only when capacity == 0 */}
                {capacity == 0 && (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                Application Form - Check List
                            </Typography>
                            <IconButton
                                onClick={handleRefresh}
                                disabled={loading}
                                color="primary"
                                sx={{
                                    bgcolor: 'primary.light',
                                    color: 'white',
                                    '&:hover': { bgcolor: 'primary.main' }
                                }}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Box>

                        {/* Error State */}
                        {
                            error && (
                                <Alert
                                    severity="error"
                                    sx={{ mb: 3 }}
                                    action={
                                        <IconButton
                                            color="inherit"
                                            size="small"
                                            onClick={() => setError(null)}
                                        >
                                            <CloseIcon fontSize="inherit" />
                                        </IconButton>
                                    }
                                >
                                    {error}
                                </Alert>
                            )
                        }

                        {/* Loading State */}
                        {
                            loading && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                                    <LinearProgress sx={{ width: '50%' }} />
                                    <Typography variant="body2" sx={{ ml: 2 }}>
                                        Loading checklist data...
                                    </Typography>
                                </Box>
                            )
                        }

                        {/* Checklist Table */}
                        {
                            !loading && checklistData.length > 0 && (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table sx={{ minWidth: 800 }}>
                                        <TableHead>
                                            <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '10%' }}>
                                                    S No.
                                                </TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '60%' }}>
                                                    Information / Details
                                                </TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '15%', textAlign: 'center' }}>
                                                    Status
                                                </TableCell>
                                                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '15%', textAlign: 'center' }}>
                                                    Availability
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {checklistData.map((item) => (
                                                <TableRow
                                                    key={item.id}
                                                    sx={{
                                                        '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
                                                        '&:hover': { backgroundColor: 'action.selected' }
                                                    }}
                                                >
                                                    <TableCell sx={{ fontWeight: 'medium' }}>
                                                        {item.sno}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Tooltip title={item.tooltip} placement="top" arrow>
                                                            <Typography variant="body2" sx={{ lineHeight: 1.4, cursor: 'help' }}>
                                                                {item.information}
                                                            </Typography>
                                                        </Tooltip>
                                                        {/* Show remarks from API if available for incomplete items */}
                                                        {item.remarks && item.chec === '~/assets/pngegg 1.svg' && (
                                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                                {item.remarks.replace(/<br \/>/g, ' ')}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            {/* Show tick/cross icons based on chec field */}
                                                            {item.chec === '~/assets/pngegg 2.svg' ? (
                                                                // Show tick icon for completed (pngegg 2)
                                                                <CheckIcon fontSize="medium" sx={{ color: 'success.main' }} />
                                                            ) : (
                                                                // Show cross icon for incomplete (pngegg 1)
                                                                <CloseIcon fontSize="medium" sx={{ color: 'error.main' }} />
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        <Chip
                                                            label={getStatusText(item.status)}
                                                            color={getStatusColor(item.status)}
                                                            size="small"
                                                            variant="filled"
                                                            sx={{
                                                                fontWeight: 'bold',
                                                                minWidth: 80
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )
                        }

                        {/* Declaration Available Notice */}
                        {
                            showDeclaration && (
                                <Alert severity="success" sx={{ mt: 3 }} icon={<CheckCircleIcon />}>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        🎉 All Enclosure Requirements Completed!
                                    </Typography>
                                    <Typography variant="body2">
                                        The Declaration section is now available in the sidebar menu. Please proceed to complete your NABL accreditation declaration.
                                    </Typography>
                                </Alert>
                            )
                        }
                    </>
                )}
            </Paper>

            {/* Submit Application Dialog */}
            <Dialog open={submitDialog} onClose={() => setSubmitDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Submit Application</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to submit the application? This action cannot be undone.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Please ensure all required documents are uploaded and information is correct.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSubmitDialog(false)}>Cancel</Button>
                    <Button onClick={handleSubmitApplication} variant="contained" color="success">
                        Submit Application
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Send to LA Dialog */}
            <Dialog open={sendToLADialog} onClose={() => setSendToLADialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Send to CAB</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Enter your password to send the application to CAB.
                    </Typography>
                    <TextField
                        label="Password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        variant="outlined"
                        fullWidth
                        required
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSendToLADialog(false)}>Cancel</Button>
                    <Button onClick={handleSendToLA} variant="contained" color="warning" disabled={!password}>
                        Send to CAB
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Notification Snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={closeNotification}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={closeNotification}
                    severity={notification.severity}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default EnclosureSection;

// import React, { useState, useEffect, useCallback } from 'react';
// import {
//     Box,
//     Paper,
//     Typography,
//     Table,
//     TableBody,
//     TableCell,
//     TableContainer,
//     TableHead,
//     TableRow,
//     Chip,
//     IconButton,
//     LinearProgress,
//     Alert,
//     Button,
//     Dialog,
//     DialogTitle,
//     DialogContent,
//     DialogActions,
//     TextField,
//     FormGroup,
//     FormControlLabel,
//     Checkbox,
//     Snackbar,
//     Tooltip,
//     Grid,
//     Divider,
//     CircularProgress,
//     FormControl,
//     InputLabel,
//     Select,
//     MenuItem,
//     Autocomplete,
//     MenuList,
//     ClickAwayListener,
//     Popper
// } from '@mui/material';
// import {
//     Check as CheckIcon,
//     Close as CloseIcon,
//     Help as HelpIcon,
//     Warning as WarningIcon,
//     Refresh as RefreshIcon,
//     CheckCircle as CheckCircleIcon,
//     Upload as UploadIcon,
//     ExpandMore as ExpandMoreIcon,
//     Error as ErrorIcon
// } from '@mui/icons-material';
// import EnclosureService from '../../../services/enclosureService';
// import FileUploadComponent from '../../../components/FileUploadComponent';
// import CalibrationlaboratoryService from '../../../services/calibrationlaboratoryService';
// import {
//     insertDANC,
//     updateDANC,
//     updateDANCBySourceId,
//     getDANCByIdEnclosure
// } from '../../../services/apiCalibration';
// import { useParams, useLocation } from 'react-router-dom';
// import { decryptText, decryptAppId } from '../../../utils/encryption';
// import {
//     getScrutinyRemarks
// } from '../../../services/api';

// // Utility function to get capacity from URL query parameters
// const getCapacityFromQuery = (locationSearch) => {
//     const searchParams = new URLSearchParams(locationSearch);
//     const encryptedCapacity = searchParams.get("capacity");
//     if (encryptedCapacity) {
//         try {
//             const decryptedCapacity = decryptText(encryptedCapacity);
//             console.log(
//                 "📍 Using decrypted capacity from URL query:",
//                 decryptedCapacity
//             );
//             return decryptedCapacity;
//         } catch (error) {
//             return encryptedCapacity;
//         }
//     }
//     return 0;
// };

// // Adequacy Check Component with DA NC Integration
// const AdequacyCheckSection = React.memo(({
//     sectionId,
//     title,
//     applicationStage,
//     orgId,
//     appId,
//     adequacyChecks,
//     handleAdequacyCheck,
//     handleAdequacyRemarks,
//     handleAdequacySubmit,
//     loading,
//     currentUser = 'System User',
//     currentUserId = 0
// }) => {
//     // Local state for DA NC operations
//     const [dancLoading, setDancLoading] = useState(false);
//     const [dancError, setDancError] = useState('');
//     const [dancSuccess, setDancSuccess] = useState('');

//     // Scrutiny remarks state
//     const [scrutinyRemarks, setScrutinyRemarks] = useState([]);
//     const [loadingRemarks, setLoadingRemarks] = useState(false);
//     const [existingDANCRecord, setExistingDANCRecord] = useState(null);
//     const [dataLoaded, setDataLoaded] = useState(false);
//     const [capacity, setCapacity] = useState(0);

//     // Checkbox dropdown state
//     const [selectedRemarksOptions, setSelectedRemarksOptions] = useState([]);
//     const [isCheckboxDropdownOpen, setIsCheckboxDropdownOpen] = useState(false);

//     // Load existing DA NC data on component mount
//     useEffect(() => {
//         const loadExistingDANCData = async () => {
//             if (!orgId || !appId) return;

//             try {
//                 setDancLoading(true);
//                 const userTypeId = localStorage.getItem('userTypeId');
//                 const expectedNCType = userTypeId === '4' ? 'Document Adequacy' : 'Assessment';
//                 console.log(`🔍 EnclosureSection - Loading DANC data for userType ${userTypeId} with expected NCtype: ${expectedNCType}`);

//                 const response = await getDANCByIdEnclosure(orgId, appId, title);
//                 console.log(`🔍 EnclosureSection - API Response:`, response);

//                 // Check if response is successful and has data array
//                 if (response && response.data && response.data.success && response.data.data && Array.isArray(response.data.data)) {
//                     const allRecords = response.data.data;
//                     console.log(`🔍 EnclosureSection - All DANC records:`, allRecords);

//                     // Filter records by NCType for current user
//                     const filteredRecords = allRecords.filter(record =>
//                         record.NCType === expectedNCType
//                     );

//                     console.log(`🔍 EnclosureSection - Filtered records for NCType "${expectedNCType}":`, filteredRecords);

//                     if (filteredRecords.length > 0) {
//                         // Get the most recent record (highest updatedate or Insertdate)
//                         const dancRecord = filteredRecords.sort((a, b) => {
//                             const dateA = new Date(a.updatedate || a.Insertdate);
//                             const dateB = new Date(b.updatedate || b.Insertdate);
//                             return dateB - dateA; // Most recent first
//                         })[0];

//                         console.log(`🔍 EnclosureSection - Selected DANC record:`, dancRecord);
//                         console.log(`🔍 EnclosureSection - DANC record matches userType ${userTypeId}, binding to dropdown...`);

//                         setExistingDANCRecord(dancRecord);

//                         // Bind DA NC data to adequacy check fields based on userTypeId
//                         if (dancRecord.InformationStatus) {
//                             console.log(`🔍 EnclosureSection - Binding InformationStatus: ${dancRecord.InformationStatus}`);
//                             handleAdequacyCheck(sectionId, dancRecord.InformationStatus);
//                         }

//                         // Bind remarks to dropdown - prioritize based on userTypeId
//                         let remarksToUse = '';
//                         if (userTypeId === '4') {
//                             // Document Adequacy user - prefer InformationProvided, fallback to Observation
//                             remarksToUse = dancRecord.InformationProvided || dancRecord.Observation || '';
//                         } else if (userTypeId === '6') {
//                             // Assessment user - prefer Observation, fallback to InformationProvided
//                             remarksToUse = dancRecord.Observation || dancRecord.InformationProvided || '';
//                         }

//                         if (remarksToUse) {
//                             console.log(`🔍 EnclosureSection - Binding remarks for userType ${userTypeId}: ${remarksToUse}`);
//                             handleAdequacyRemarks(sectionId, remarksToUse);
//                         }

//                         console.log(`🔍 EnclosureSection - Successfully bound DANC record to dropdown for userType ${userTypeId}`);
//                     } else {
//                         console.log(`🔍 EnclosureSection - No DANC records found for NCType "${expectedNCType}"`);
//                         setExistingDANCRecord(null);
//                     }
//                 } else {
//                     console.log('🔍 EnclosureSection - No DANC records found or invalid response structure');
//                     setExistingDANCRecord(null);
//                 }
//                 setDataLoaded(true);
//             } catch (error) {
//                 console.log('🔍 EnclosureSection - No existing DA NC record found or error loading:', error.message);
//                 setExistingDANCRecord(null);
//                 setDataLoaded(true);
//             } finally {
//                 setDancLoading(false);
//             }
//         };

//         if (!dataLoaded) {
//             loadExistingDANCData();
//         }
//     }, [
//         orgId,
//         appId,
//         title,
//         sectionId,
//         dataLoaded,
//         handleAdequacyCheck,
//         handleAdequacyRemarks
//     ]);

//     // Fetch scrutiny remarks
//     useEffect(() => {
//         fetchScrutinyRemarks();
//     }, [title]); // Add title dependency to refetch when title changes

//     const fetchScrutinyRemarks = async () => {
//         setLoadingRemarks(true);
//         try {
//             const responseApi = await getScrutinyRemarks({
//                 sectionName: title, // Use dynamic title instead of hardcoded value
//                 type: localStorage.getItem('userTypeId') === '6' ? 'Assessment' : 'Document Review'
//             });
//             const response = responseApi.data;
//             if (response.success && response.data) {
//                 setScrutinyRemarks(response.data);
//                 console.log('Enclosure: Loaded scrutiny remarks:', response.data.length);
//             } else {
//                 setScrutinyRemarks([]);
//             }
//         } catch (error) {
//             console.error('Error fetching scrutiny remarks:', error);
//             setScrutinyRemarks([]);
//         } finally {
//             setLoadingRemarks(false);
//         }
//     };
//       // Function to get assessorId from URL query parameters with decryption
//         // const getAssessorIdFromQuery = () => {
//         //   const searchParams = new URLSearchParams(location.search);
//         //   const encryptedAssessorId = searchParams.get("assessorId");
//         //   if (encryptedAssessorId) {
//         //     try {
//         //       const decryptedAssessorId = decryptText(encryptedAssessorId);
//         //       console.log(
//         //         "📍 Using decrypted assessorId from URL query:",
//         //         decryptedAssessorId
//         //       );
//         //       return decryptedAssessorId;
//         //     } catch (error) {
//         //       return encryptedAssessorId;
//         //     }
//         //   }
//         //   return 0;
//         // };

//         // const assessorId = getAssessorIdFromQuery();

//     // Clear messages after timeout
//     useEffect(() => {
//         if (dancError || dancSuccess) {
//             const timer = setTimeout(() => {
//                 setDancError('');
//                 setDancSuccess('');
//             }, 5000);
//             return () => clearTimeout(timer);
//         }
//     }, [dancError, dancSuccess]);

//     // Update selected options when adequacy remarks change
//     useEffect(() => {
//         const currentRemarks = adequacyChecks[sectionId]?.remarks || '';
//         if (currentRemarks) {
//             const remarksArray = currentRemarks.split(', ').map(remark => remark.trim()).filter(remark => remark !== '');
//             setSelectedRemarksOptions(remarksArray);
//         } else {
//             setSelectedRemarksOptions([]);
//         }
//     }, [adequacyChecks, sectionId]);

//     const userTypeId = localStorage.getItem('userTypeId');
//     if ((applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") && (userTypeId !== '4' && userTypeId !== '6')) return null;
//     if (applicationStage !== "18" && applicationStage !== "18a" && applicationStage !== "18b" && applicationStage !== "20") return null;

//     // Check if adequacy checks should be read-only (stage 18a)
//     const isAdequacyReadOnly = applicationStage === "18a";

//     const adequacy = adequacyChecks[sectionId] || { status: '', remarks: '' };

//     // Standard status change handler
//     const handleStatusChange = (status) => {
//         handleAdequacyCheck(sectionId, status);
//     };

//     // Standard remarks change handler
//     const handleRemarksChange = (remarks) => {
//         handleAdequacyRemarks(sectionId, remarks);
//     };

//     // Checkbox dropdown handlers
//     const handleCheckboxChange = (remark, isChecked) => {
//         let newSelectedOptions;
//         if (isChecked) {
//             newSelectedOptions = [...selectedRemarksOptions, remark];
//         } else {
//             newSelectedOptions = selectedRemarksOptions.filter(option => option !== remark);
//         }
//         setSelectedRemarksOptions(newSelectedOptions);
        
//         // Update the remarks field with comma-separated values
//         const commaSeparatedRemarks = newSelectedOptions.join(', ');
//         handleAdequacyRemarks(sectionId, commaSeparatedRemarks);
//     };

//     const handleClearAllCheckboxes = () => {
//         setSelectedRemarksOptions([]);
//         handleAdequacyRemarks(sectionId, '');
//     };

//     // Enhanced submit handler with DA NC API call
//     const handleSubmit = async () => {
//         try {
//             setDancLoading(true);
//             setDancError('');
//             setDancSuccess('');

//             // First submit the adequacy check
//             await handleAdequacySubmit(sectionId);
//              const NCtype = userTypeId === '4' ? 'Document Adequacy' : 'Assessment';
//             // Then create/update DA NC record based on status
//             if (adequacy.status) {
//                 if (existingDANCRecord) {
//                     // Update existing record
//                     try {
//                         const updateData = {
//                             subsectionName: sectionId,
//                             informationStatus: adequacy.status,
//                             observation: adequacy.status === 'Inadequate'
//                                 ? (adequacy.remarks || 'Document adequacy marked as Inadequate')
//                                 : 'Document adequacy check completed',
//                             isactive: 1,
//                             NCtype: NCtype,
//                             sourceid: 0
//                         };

//                         // Get assessorId and assessmentId (defaulting to 0 if not available)
//                         const assessorId = localStorage.getItem('orgId') || 0;
//                         const assessmentId = 0; // Default assessmentId for enclosure section

//                         const response = await updateDANCBySourceId(orgId, appId, title, 0, assessmentId, assessorId, updateData);

//                         if (response.success) {
//                             setExistingDANCRecord(response.data);
//                         } else {
//                             throw new Error(response.message || 'Update failed');
//                         }
//                     } catch (updateError) {
//                         console.error('DA-NC update failed:', updateError);
//                     }
//                 } else {
//                     // Create new record with userTypeId-based NCtype
//                     try {
//                         const userTypeId = localStorage.getItem('userTypeId');
//                         const NCtype = userTypeId === '4' ? 'Document Adequacy' : 'Assessment';
//                         console.log(`🔍 EnclosureSection - Creating DANC record for userType ${userTypeId} with NCtype: ${NCtype}`);

//                         const dancData = {
//                             Orgid: parseInt(orgId),
//                             Appid: parseInt(appId),
//                             SectionName: title,
//                             SubsectionName: sectionId,
//                             InformationProvided: adequacy.remarks || '',
//                             InformationStatus: adequacy.status,
//                             Observation: adequacy.status === 'Inadequate'
//                                 ? (adequacy.remarks || 'Document adequacy marked as Inadequate')
//                                 : 'Document adequacy check completed',
//                             Insertedby: localStorage.getItem('orgId') || 0,
//                             sourceid: 0,
//                             NCtype: NCtype,
//                         };

//                         const response = await insertDANC(dancData);

//                         if (response.data.success) {
//                             setExistingDANCRecord(response.data.data);
//                             setDancSuccess('Record created successfully');
//                         } else {
//                             throw new Error(response.message || 'Insert failed');
//                         }
//                     } catch (insertError) {
//                         console.error('DA-NC creation failed:', insertError);
//                         setDancError('Failed to create Record: ' + insertError.message);
//                     }
//                 }
//             }
//         } catch (error) {
//             console.error('Error submitting adequacy check:', error);
//             setDancError('Error submitting adequacy check: ' + error.message);
//         } finally {
//             setDancLoading(false);
//         }
//     };

//     return (
//         <Box sx={{
//             mb: 3,
//             p: 3,
//             backgroundColor: '#f8f9fa',
//             borderRadius: 2,
//             border: '2px solid #e9ecef',
//             borderLeft: '4px solid #007bff'
//         }}>
//             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
//                 <Typography variant="h6" sx={{
//                     color: '#495057',
//                     fontWeight: 600,
//                     fontSize: '1.1rem',
//                     display: 'flex',
//                     alignItems: 'center',
//                     gap: 1
//                 }}>
//                     📋 Adequacy Check - {title}
//                     {isAdequacyReadOnly && (
//                         <Chip
//                             label="🔒 Read Only"
//                             size="small"
//                             sx={{
//                                 bgcolor: '#f5f5f5',
//                                 color: '#757575',
//                                 fontWeight: 'bold',
//                                 fontSize: '0.7rem'
//                             }}
//                         />
//                     )}
//                 </Typography>

//                 {(adequacy.status || existingDANCRecord) && (
//                     <Chip
//                         label={`DA-NC: ${adequacy.status || existingDANCRecord?.InformationStatus || 'Active'}`}
//                         size="small"
//                         sx={{
//                             bgcolor: (adequacy.status || existingDANCRecord?.InformationStatus) === 'Adequate' ? '#28a745' : '#dc3545',
//                             color: 'white',
//                             fontWeight: 'bold'
//                         }}
//                     />
//                 )}
//             </Box>

//             {/* DA NC Error/Success Messages */}
//             {dancError && (
//                 <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDancError('')}>
//                     {dancError}
//                 </Alert>
//             )}

//             {dancSuccess && (
//                 <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDancSuccess('')}>
//                     {dancSuccess}
//                 </Alert>
//             )}

//             <Grid container spacing={3} alignItems="flex-start">
//                 <Grid item xs={12} md={4}>
//                     <FormControl fullWidth sx={{
//                         '& .MuiOutlinedInput-root': {
//                             backgroundColor: 'white',
//                             borderRadius: 2,
//                             '&:hover': {
//                                 boxShadow: '0 2px 8px rgba(0,123,255,0.15)',
//                             },
//                             '&.Mui-focused': {
//                                 boxShadow: '0 4px 12px rgba(0,123,255,0.25)',
//                             }
//                         }
//                     }}>
//                         <InputLabel>Document Status</InputLabel>
//                         <Select
//                             value={adequacy.status}
//                             onChange={isAdequacyReadOnly ? undefined : (e) => handleStatusChange(e.target.value)}
//                             label="Document Status"
//                             disabled={isAdequacyReadOnly}
//                             sx={isAdequacyReadOnly ? {
//                                 backgroundColor: '#f5f5f5',
//                                 cursor: 'not-allowed',
//                                 '& .MuiOutlinedInput-notchedOutline': {
//                                     borderColor: '#e0e0e0',
//                                 },
//                                 '&:hover .MuiOutlinedInput-notchedOutline': {
//                                     borderColor: '#e0e0e0',
//                                 }
//                             } : undefined}
//                         >
//                             <MenuItem value="">-- Select Status --</MenuItem>
//                             <MenuItem value="Adequate">
//                                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                                     <CheckCircleIcon sx={{ color: '#28a745', fontSize: '1rem' }} />
//                                     Adequate
//                                 </Box>
//                             </MenuItem>
//                             <MenuItem value="Inadequate">
//                                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                                     <ErrorIcon sx={{ color: '#dc3545', fontSize: '1rem' }} />
//                                     Inadequate
//                                 </Box>
//                             </MenuItem>
//                             {userTypeId === '4' && (
//                             <MenuItem value="To be Verify">
//                                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                                     <ErrorIcon sx={{ color: '#dc3545', fontSize: '1rem' }} />
//                                     To be Verify
//                                 </Box>
//                             </MenuItem>
//                             )}
//                         </Select>
//                     </FormControl>
//                 </Grid>

//                 {adequacy.status === 'Inadequate' && (
//                     <Grid item xs={12} md={8}>
//                         {isAdequacyReadOnly ? (
//                             <TextField
//                                 fullWidth
//                                 multiline
//                                 rows={3}
//                                 label="Remarks (View Only)"
//                                 placeholder="View Only (Read-only)"
//                                 value={adequacy.remarks || ''}
//                                 required={false}
//                                 variant="outlined"
//                                 InputProps={{
//                                     readOnly: true,
//                                     style: {
//                                         backgroundColor: '#f5f5f5',
//                                         cursor: 'not-allowed'
//                                     }
//                                 }}
//                                 sx={{
//                                     '& .MuiOutlinedInput-root': {
//                                         backgroundColor: '#f5f5f5',
//                                         borderRadius: 2,
//                                         cursor: 'not-allowed',
//                                     },
//                                 }}
//                             />
//                         ) : (
//                             <Box sx={{ position: 'relative' }}>
//                                 <TextField
//                                     fullWidth
//                                     multiline
//                                     rows={3}
//                                     label={`Remarks (Required)${scrutinyRemarks.length > 0 ? ` - ${scrutinyRemarks.length} suggestions available` : ''}`}
//                                     placeholder="Type your remarks here..."
//                                     value={adequacy.remarks || ''}
//                                     onChange={(e) => handleRemarksChange(e.target.value)}
//                                     required
//                                     variant="outlined"
//                                     sx={{
//                                         '& .MuiOutlinedInput-root': {
//                                             backgroundColor: 'white',
//                                             borderRadius: 2,
//                                             '&:hover': {
//                                                 boxShadow: '0 2px 8px rgba(220,53,69,0.15)',
//                                             },
//                                             '&.Mui-focused': {
//                                                 boxShadow: '0 4px 12px rgba(220,53,69,0.25)',
//                                             }
//                                         }
//                                     }}
//                                 />
                                
//                                 {/* Checkbox Dropdown */}
//                                 {scrutinyRemarks.length > 0 && (
//                                     <Box sx={{ mt: 1 }}>
//                                         <Button
//                                             variant="outlined"
//                                             size="small"
//                                             onClick={() => setIsCheckboxDropdownOpen(!isCheckboxDropdownOpen)}
//                                             sx={{
//                                                 textTransform: 'none',
//                                                 borderColor: '#1976d2',
//                                                 color: '#1976d2',
//                                                 '&:hover': {
//                                                     borderColor: '#1565c0',
//                                                     backgroundColor: '#e3f2fd'
//                                                 }
//                                             }}
//                                         >
//                                             {isCheckboxDropdownOpen ? '🔽 Hide' : '🔽 Show'} Remarks Options ({scrutinyRemarks.length})
//                                         </Button>
                                        
//                                         {selectedRemarksOptions.length > 0 && (
//                                             <Button
//                                                 variant="text"
//                                                 size="small"
//                                                 onClick={handleClearAllCheckboxes}
//                                                 sx={{ 
//                                                     ml: 1, 
//                                                     color: '#d32f2f',
//                                                     textTransform: 'none',
//                                                     '&:hover': {
//                                                         backgroundColor: '#ffebee'
//                                                     }
//                                                 }}
//                                             >
//                                                 Clear All ({selectedRemarksOptions.length})
//                                             </Button>
//                                         )}
                                        
//                                         {isCheckboxDropdownOpen && (
//                                             <ClickAwayListener onClickAway={() => setIsCheckboxDropdownOpen(false)}>
//                                                 <Paper
//                                                     elevation={3}
//                                                     sx={{
//                                                         mt: 1,
//                                                         maxHeight: 300,
//                                                         overflow: 'auto',
//                                                         border: '1px solid #e0e0e0',
//                                                         borderRadius: 2
//                                                     }}
//                                                 >
//                                                     <MenuList dense>
//                                                         {scrutinyRemarks.map((remark, index) => {
//                                                             const remarkText = remark.inadequate_remarks || '';
//                                                             const isSelected = selectedRemarksOptions.includes(remarkText);
                                                            
//                                                             return (
//                                                                 <MenuItem
//                                                                     key={index}
//                                                                     onClick={() => handleCheckboxChange(remarkText, !isSelected)}
//                                                                     sx={{
//                                                                         py: 1,
//                                                                         px: 2,
//                                                                         '&:hover': {
//                                                                             backgroundColor: '#f5f5f5'
//                                                                         }
//                                                                     }}
//                                                                 >
//                                                                     <Checkbox
//                                                                         checked={isSelected}
//                                                                         onChange={(e) => handleCheckboxChange(remarkText, e.target.checked)}
//                                                                         size="small"
//                                                                         color="primary"
//                                                                         sx={{ mr: 1 }}
//                                                                     />
//                                                                     <Typography variant="body2" sx={{ flex: 1 }}>
//                                                                         {remarkText}
//                                                                     </Typography>
//                                                                 </MenuItem>
//                                                             );
//                                                         })}
//                                                     </MenuList>
//                                                 </Paper>
//                                             </ClickAwayListener>
//                                         )}
//                                     </Box>
//                                 )}
//                             </Box>
//                         )}
//                         <Typography variant="caption" sx={{
//                             color: '#6c757d',
//                             display: 'block',
//                             mt: 1,
//                             fontStyle: 'italic'
//                         }}>
//                             {isAdequacyReadOnly
//                                 ? "Adequacy check is in read-only mode for stage 18a."
//                                 : (scrutinyRemarks.length > 0
//                                     ? `${scrutinyRemarks.length} existing remark(s) available for "${title}". You can select from checkbox dropdown or type your own remarks.`
//                                     : "Please specify what needs to be corrected or improved."
//                                 )
//                             }
//                         </Typography>
//                     </Grid>
//                 )}
//             </Grid>

//             {adequacy.status && (
//                 <Box sx={{ mt: 2 }}>
//                     <Alert
//                         severity={existingDANCRecord
//                             ? (adequacy.status === 'Adequate' ? 'info' : 'warning')
//                             : (adequacy.status === 'Adequate' ? 'success' : 'warning')
//                         }
//                         sx={{
//                             borderRadius: 2,
//                             '& .MuiAlert-icon': {
//                                 fontSize: '1.2rem'
//                             }
//                         }}
//                     >
//                         {isAdequacyReadOnly
//                             ? '🔒 Adequacy check is in read-only mode for stage 18a. No modifications can be made.'
//                             : (existingDANCRecord
//                                 ? (adequacy.status === 'Adequate'
//                                     ? `✓ Record exists. Click Update to modify.`
//                                     : `⚠ Record exists. Click Update to modify.`)
//                                 : (adequacy.status === 'Adequate'
//                                     ? '✓ This section is marked as Adequate. Click Submit to create Record.'
//                                     : '⚠ Please address the remarks above and submit to create Record.'))}
//                     </Alert>

//                     <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
//                         <Button
//                             variant="contained"
//                             size="medium"
//                             onClick={handleSubmit}
//                             disabled={
//                                 isAdequacyReadOnly ||
//                                 !adequacy.status ||
//                                 (adequacy.status === 'Inadequate' && !adequacy.remarks?.trim()) ||
//                                 loading ||
//                                 dancLoading
//                             }
//                             sx={{
//                                 background: existingDANCRecord
//                                     ? 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)'
//                                     : (adequacy.status === 'Adequate'
//                                         ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
//                                         : 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)'),
//                                 color: 'white',
//                                 fontWeight: 600,
//                                 px: 3,
//                                 py: 1,
//                                 borderRadius: 2,
//                                 boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
//                                 '&:hover': {
//                                     transform: 'translateY(-2px)',
//                                     boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
//                                 },
//                                 '&:disabled': {
//                                     background: '#e9ecef',
//                                     color: '#6c757d',
//                                     transform: 'none',
//                                     boxShadow: 'none'
//                                 }
//                             }}
//                         >
//                             {(loading || dancLoading) ? (
//                                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                                     <CircularProgress size={16} sx={{ color: 'white' }} />
//                                     {existingDANCRecord ? 'Updating...' : 'Submitting...'}
//                                 </Box>
//                             ) : (
//                                 <>📤 {existingDANCRecord ? 'Update Adequacy Check' : 'Submit Adequacy Check'}</>
//                             )}
//                         </Button>
//                     </Box>
//                 </Box>
//             )}
//         </Box>
//     );
// });

// const EnclosureSection = ({ data, onUpdate, allFormData = {} }) => {
//     // Router hooks for encrypted parameters
//     const { orgId: encryptedOrgId, appId: encryptedAppId } = useParams();
//     const location = useLocation();
//     // Extract encrypted parameters from URL query params or route params
//     const getOrgId = useCallback(() => {
//         try {
//             // First try to get from URL query parameters
//             const urlParams = new URLSearchParams(location.search);
//             const orgIdParam = urlParams.get('orgId');
//             if (orgIdParam) {
//                 return decryptText(orgIdParam);
//             }

//             // Then try to get from route parameters
//             if (encryptedOrgId) {
//                 return decryptText(encryptedOrgId);
//             }

//             return null;
//         } catch (error) {
//             console.error('EnclosureSection - Error decrypting orgId:', error);
//             return null;
//         }
//     }, [encryptedOrgId, location.search]);

//     const getAppId = useCallback(() => {
//         try {
//             // First try to get from URL query parameters
//             const urlParams = new URLSearchParams(location.search);
//             const appIdParam = urlParams.get('appId');
//             if (appIdParam) {
//                 return decryptAppId(appIdParam);
//             }

//             // Then try to get from route parameters
//             if (encryptedAppId) {
//                 return decryptAppId(encryptedAppId);
//             }

//             return null;
//         } catch (error) {
//             console.error('EnclosureSection - Error decrypting appId:', error);
//             return null;
//         }
//     }, [encryptedAppId, location.search]);

//     // Get capacity from URL query parameters
//     const capacity = getCapacityFromQuery(location.search);

//     // State management - Updated to fix getChecklistStatus error
//     const [checklistData, setChecklistData] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState(null);
//     const [notification, setNotification] = useState({
//         open: false,
//         message: '',
//         severity: 'info'
//     });

//     // Application state
//     const [applicationStage, setapplicationStage] = useState(null);
//     const [isFreezed, setIsFreezed] = useState(false);
//     const [labTypeId, setLabTypeId] = useState(2);
//     const [standard, setStandard] = useState(2017);

//     // Declaration states
//     const [showDeclaration, setShowDeclaration] = useState(false);
//     const [declarationData, setDeclarationData] = useState({
//         orgDeclaration: false,
//         orgName: '',
//         adverseDeclaration: false,
//         adverseDetails: ''
//     });

//     // Dialog states
//     const [submitDialog, setSubmitDialog] = useState(false);
//     const [sendToLADialog, setSendToLADialog] = useState(false);
//     const [password, setPassword] = useState('');

//     // File upload state
//     const [uploadedFile, setUploadedFile] = useState(null);
//     const [saving, setSaving] = useState(false);

//     // Adequacy Check States
//     const [adequacyChecks, setAdequacyChecks] = useState({});
//     const [adequacyLoading, setAdequacyLoading] = useState(false);

//     // Adequacy Check Handlers
//     const handleAdequacyCheck = (sectionId, status) => {
//         setAdequacyChecks(prev => ({
//             ...prev,
//             [sectionId]: {
//                 ...(prev[sectionId] || {}),
//                 status: status
//             }
//         }));
//     };

//     const handleAdequacyRemarks = (sectionId, remarks) => {
//         setAdequacyChecks(prev => ({
//             ...prev,
//             [sectionId]: {
//                 ...(prev[sectionId] || {}),
//                 remarks: remarks
//             }
//         }));
//     };

//     const handleAdequacySubmit = async (sectionId) => {
//         setAdequacyLoading(true);
//         return new Promise((resolve) => {
//             setTimeout(() => {
//                 setAdequacyChecks(prev => ({
//                     ...prev,
//                     [sectionId]: {
//                         ...prev[sectionId],
//                         isSubmitted: true,
//                         submittedAt: new Date().toISOString()
//                     }
//                 }));
//                 setAdequacyLoading(false);
//                 resolve();
//             }, 500);
//         });
//     };

//     // Load adequacy check data from API
//     const loadAdequacyCheckData = async () => {
//         try {
//             setLoading(true);
//             setError(null);

//             const response = await CalibrationlaboratoryService.getLaboratoryAdequacyCheck(getAppId(), getOrgId(), labTypeId);

//             if (response.success && response.data) {
//                 // Transform API response to match our component structure
//                 const transformedData = response.data.map((item, index) => ({
//                     id: index + 1,
//                     sno: index + 1,
//                     information: item.line,
//                     status: item.chec === '~/assets/pngegg 2.svg' ? 'completed' : 'not-filled', // Use chec field: pngegg 2 = completed, pngegg 1 = not-filled
//                     chec: item.chec, // Use the chec field directly from API response
//                     show: item.show,
//                     remarks: item.Remarks,
//                     link: item.link,
//                     tooltip: item.Remarks ? item.Remarks.replace(/<br \/>/g, ' ') : `Complete this section: ${item.line}`
//                 }));

//                  setChecklistData(transformedData);

//                 // Check completion status
//                 const allComplete = transformedData.every(item => item.status === 'completed');
//                 setShowDeclaration(allComplete);

//                 // Update localStorage to inform layout about completion
//                 localStorage.setItem('medicalEnclosureCompleted', allComplete ? 'true' : 'false');

//                 // Trigger a custom event to immediately update the layout
//                 window.dispatchEvent(new CustomEvent('enclosureStatusChanged', {
//                     detail: { completed: allComplete }
//                 }));

//             } else {
//                 throw new Error(response.message || 'Invalid API response format');
//             }

//         } catch (error) {
//             console.error('Error loading adequacy check data:', error);
//             setError(error.message || 'Failed to load adequacy check data');
//             showNotification('Error loading checklist data. Using default validation.', 'warning');

//         } finally {
//             setLoading(false);
//         }
//     };

//     // Fallback validation functions (same as before)
//     const validateLegalEntityDocuments = () => {
//         const labData = allFormData.laboratoryDetails || {};
//         const orgData = allFormData.organization || {};

//         if (!labData.legalEntityType) return 'not-filled';

//         const hasEntityDocument =
//             labData.proprietorshipCertificateFile ||
//             labData.partnershipDeedFile ||
//             labData.llpCertificateFile ||
//             labData.companiesActCertificateFile ||
//             labData.publicLtdCertificateFile ||
//             labData.governmentCertificateFile ||
//             labData.trustCertificateFile ||
//             labData.societyCertificateFile ||
//             orgData.managementSystemDocument;

//         return hasEntityDocument ? 'completed' : 'not-filled';
//     };

//     const validateScopeAndTestMethods = () => {
//         const scopeData = allFormData.scope || {};
//         const hasScope = scopeData.scopes && scopeData.scopes.length > 0;
//         const hasMethods = scopeData.testMethods && scopeData.testMethods.length > 0;

//         if (hasScope && hasMethods) return 'completed';
//         if (hasScope || hasMethods) return 'partial';
//         return 'not-filled';
//     };

//     const validateSeniorManagementDetails = () => {
//         const personnelData = allFormData.personnel || {};
//         const labStaffData = allFormData.labStaff || {};

//         const hasManagement =
//             (personnelData.seniorManagement && personnelData.seniorManagement.length > 0) ||
//             (labStaffData.staff && labStaffData.staff.some(staff =>
//                 staff.designation && staff.designation.toLowerCase().includes('manager')
//             ));

//         return hasManagement ? 'completed' : 'not-filled';
//     };

//     const validateStaffDetails = () => {
//         const labStaffData = allFormData.labStaff || {};
//         const personnelData = allFormData.personnel || {};

//         const hasStaff =
//             (labStaffData.staff && labStaffData.staff.length > 0) ||
//             (personnelData.staff && personnelData.staff.length > 0);

//         const hasAuthorizationDetails = labStaffData.staff &&
//             labStaffData.staff.some(staff => staff.reportReview === 'Yes');

//         if (hasStaff && hasAuthorizationDetails) return 'completed';
//         if (hasStaff) return 'partial';
//         return 'not-filled';
//     };

//     const validateOrganizationChart = () => {
//         const orgData = allFormData.organization || {};
//         const labData = allFormData.laboratoryDetails || {};

//         const hasOrgChart =
//             orgData.organizationChart ||
//             orgData.parentOrganizationChart ||
//             labData.organizationChartFile;

//         return hasOrgChart ? 'completed' : 'not-filled';
//     };

//     const validateEquipmentList = () => {
//         const equipmentData = allFormData.equipment || {};
//         const hasEquipment = equipmentData.equipmentList && equipmentData.equipmentList.length > 0;
//         const hasTraceability = equipmentData.equipmentList &&
//             equipmentData.equipmentList.some(eq => eq.traceability || eq.calibrationCertificate);

//         if (hasEquipment && hasTraceability) return 'completed';
//         if (hasEquipment) return 'partial';
//         return 'not-filled';
//     };

//     const validateQualityControlActivity = () => {
//         const ptData = allFormData.proficiencyTesting || {};
//         const labData = allFormData.laboratoryDetails || {};

//         const hasPTParticipation = ptData.programmes && ptData.programmes.length > 0;
//         const hasQCActivity =
//             labData.qualityControlProgram ||
//             labData.internalQualityControl ||
//             hasPTParticipation;

//         return hasQCActivity ? 'completed' : 'not-filled';
//     };

//     const validateInternalAuditManagementReview = () => {
//         const labData = allFormData.laboratoryDetails || {};

//         const hasInternalAudit =
//             labData.internalAuditFromDate && labData.internalAuditToDate;
//         const hasManagementReview =
//             labData.managementReviewFromDate && labData.managementReviewToDate;
//         const hasAuditDocument = labData.internalAuditDocument;
//         const hasReviewDocument = labData.managementReviewDocument;

//         if (hasInternalAudit && hasManagementReview && hasAuditDocument && hasReviewDocument) {
//             return 'completed';
//         }
//         if (hasInternalAudit || hasManagementReview) return 'partial';
//         return 'not-filled';
//     };

//     const validateManagementSystemDocument = () => {
//         const orgData = allFormData.organization || {};
//         const labData = allFormData.laboratoryDetails || {};

//         const hasQualityManual =
//             orgData.managementSystemDocument ||
//             labData.qualityManualFile ||
//             labData.managementSystemFile;

//         return hasQualityManual ? 'completed' : 'not-filled';
//     };



//     // Initialize data
//     useEffect(() => {
//         // Load adequacy check data from API first
//         loadAdequacyCheckData();

//         // Determine application stage and permissions
//         determineApplicationStage();

//         // Load previously uploaded file if available
//         if (data && data.uploadedFile) {
//             setUploadedFile(data.uploadedFile);
//         }

//         // Load existing QMS file from database
//         loadQMSFile();
//     }, []);

//     // Load existing QMS file from laboratory API
//     const loadQMSFile = async () => {
//         try {
//             const response = await CalibrationlaboratoryService.getLaboratoryDetails(getOrgId());
//             console.log('Laboratory Details API Response:', response);
//             console.log('Full lab data:', response?.data);

//             // Extract QMS file from laboratory details - check all possible field names
//             const labData = response?.data;


//             const qmsFileName = labData?.QMSFile || labData?.QMSFile || labData?.QMSFile || labData?.QMSFile;

//             if (qmsFileName && qmsFileName !== '') {
//                 console.log('Found QMS file in laboratory details:', qmsFileName);

//                 // Create file data object with the filename
//                 // The key should be 'live/{filename}' to match S3 storage location
//                 const fileData = {
//                     fileName: qmsFileName,
//                     key: `live/${qmsFileName}`,  // Files are stored in live/ folder directly
//                     originalName: qmsFileName,
//                     storage: 'S3',
//                     uploadDate: labData?.lastUpdated || labData?.updatedAt || new Date().toISOString()
//                 };

//                 setUploadedFile(fileData);
//                 console.log('QMS file loaded and set:', fileData);
//             } else {
//                 console.log('No QMS file found in laboratory details');
//                 console.log('Searched for fields: QMS, qms, qmsFile, Qms');
//             }
//         } catch (error) {
//             console.error('Error loading QMS file from laboratory details:', error);
//             // Don't show error notification, file might not exist yet
//         }
//     };

//     const determineApplicationStage = async () => {
//         try {
//             // Fetch application stage from API
//             const appId = getAppId();
//             if (appId) {
//                 const response = await fetch(`${process.env.REACT_APP_API_URL}/laboratory/application-stage/${appId}`);
//                 const result = await response.json();

//                 if (result.success && result.stage) {
//                     setapplicationStage(result.stage);
//                     console.log('Application stage loaded:', result.stage);
//                 } else {
//                     setapplicationStage(null); // Stage not found, set to null
//                     console.log('Stage not found, setting to null');
//                 }
//             } else {
//                 setapplicationStage(null); // No appId, set to null
//             }

//             setIsFreezed(false); // Default to not frozen

//             // Show declaration if all checklist items are complete
//             const stats = getCompletionStats();
//             const allComplete = stats.pending === 0 && stats.total > 0;
//             setShowDeclaration(allComplete);

//             // Update localStorage to inform layout about completion
//             localStorage.setItem('medicalEnclosureCompleted', allComplete ? 'true' : 'false');

//             // Trigger a custom event to immediately update the layout
//             window.dispatchEvent(new CustomEvent('enclosureStatusChanged', {
//                 detail: { completed: allComplete }
//             }));

//         } catch (error) {
//             console.error('Error determining application stage:', error);
//             // Use null as default if API fails
//             setapplicationStage(null);
//             setIsFreezed(false);
//         }
//     };

//     // Helper functions for UI
//     const getStatusIcon = (status) => {
//         switch (status) {
//             case 'completed':
//                 return <CheckIcon fontSize="medium" sx={{ color: 'success.main' }} />;
//             case 'partial':
//                 return <WarningIcon fontSize="medium" sx={{ color: 'warning.main' }} />;
//             case 'not-applicable':
//                 return <HelpIcon fontSize="medium" sx={{ color: 'info.main' }} />;
//             case 'not-filled':
//             default:
//                 return <CloseIcon fontSize="medium" sx={{ color: 'error.main' }} />;
//         }
//     };

//     const getStatusColor = (status) => {
//         switch (status) {
//             case 'completed':
//                 return 'success';
//             case 'partial':
//                 return 'warning';
//             case 'not-applicable':
//                 return 'info';
//             case 'not-filled':
//             default:
//                 return 'error';
//         }
//     };

//     const getStatusText = (status) => {
//         switch (status) {
//             case 'completed':
//                 return 'Complete';
//             case 'partial':
//                 return 'Partial';
//             case 'not-applicable':
//                 return 'N/A';
//             case 'not-filled':
//             default:
//                 return 'Pending';
//         }
//     };

//     // Calculate overall completion percentage
//     const getCompletionPercentage = () => {
//         const applicableItems = checklistData.filter(item => item.status !== 'not-applicable');
//         const completedItems = applicableItems.filter(item => item.status === 'completed');
//         const partialItems = applicableItems.filter(item => item.status === 'partial');

//         if (applicableItems.length === 0) return 100;

//         const score = (completedItems.length * 1) + (partialItems.length * 0.5);
//         return Math.round((score / applicableItems.length) * 100);
//     };

//     // Show notification
//     const showNotification = (message, severity = 'info') => {
//         setNotification({
//             open: true,
//             message,
//             severity
//         });
//     };

//     // Close notification
//     const closeNotification = () => {
//         setNotification(prev => ({ ...prev, open: false }));
//     };

//     // Handle file upload
//     const handleFileUpload = (fieldName, uploadData) => {
//         console.log(`File uploaded for ${fieldName}:`, uploadData);

//         if (uploadData && uploadData.key) {
//             const fileData = {
//                 fileName: uploadData.fileName || uploadData.originalName,
//                 key: uploadData.key,
//                 originalName: uploadData.originalName,
//                 fileSize: uploadData.fileSize,
//                 uploadDate: uploadData.uploadDate || new Date().toISOString(),
//                 storage: 'S3'
//             };

//             setUploadedFile(fileData);

//             // Notify parent component if needed
//             if (onUpdate) {
//                 onUpdate({
//                     ...data,
//                     uploadedFile: fileData
//                 });
//             }

//             showNotification('File uploaded successfully to S3. Click "Save File" to save permanently.', 'success');
//         } else if (uploadData === null) {
//             // File was deleted
//             setUploadedFile(null);

//             if (onUpdate) {
//                 onUpdate({
//                     ...data,
//                     uploadedFile: null
//                 });
//             }

//             showNotification('File removed successfully', 'info');
//         }
//     };

//     // Save file name to database (QMS column)
//     const handleSaveToDatabase = async () => {
//         if (!uploadedFile) {
//             showNotification('Please upload a file first', 'warning');
//             return;
//         }

//         setSaving(true);
//         try {
//             // Extract just the filename without any path prefixes
//             let fileNameToSave = uploadedFile.key || uploadedFile.fileName;

//             // Remove 'live/' prefix if it exists
//             if (fileNameToSave.startsWith('live/')) {
//                 fileNameToSave = fileNameToSave.replace('live/', '');
//             }

//             // Remove any other path components (applications/orgId/section/)
//             // Extract just the last part after the last '/'
//             if (fileNameToSave.includes('/')) {
//                 const parts = fileNameToSave.split('/');
//                 fileNameToSave = parts[parts.length - 1]; // Get the last part (filename only)
//             }

//             console.log('Saving filename to database:', fileNameToSave);

//             // Save the file key/name to the QMS column in the database
//             const result = await EnclosureService.updateQMSFile(
//                 getAppId(),
//                 getOrgId(),
//                 fileNameToSave
//             );

//             if (result.success) {
//                 showNotification('File saved to database successfully', 'success');
//             } else {
//                 showNotification(result.message || 'Failed to save file to database', 'error');
//             }
//         } catch (error) {
//             console.error('Error saving file to database:', error);
//             showNotification(error.message || 'Failed to save file to database', 'error');
//         } finally {
//             setSaving(false);
//         }
//     };

//     // Application submission handlers
//     const handlePreviewReport = () => {
//         showNotification('Opening preview report...', 'info');
//         // Navigate to preview report page
//         window.open('/medical/preview-report', '_blank');
//     };

//     const handleSubmitApplication = async () => {
//         try {
//             // Check if Management System Document is uploaded in organization section
//             const orgData = allFormData.organization || {};
//             if (!orgData.managementSystemDocument) {
//                 showNotification('Please upload Management System Document in Organization section for final submission', 'error');
//                 return;
//             }

//             // Prepare submission data
//             const submissionData = {
//                 orgId: getOrgId(),
//                 appId: getAppId(),
//                 orgDeclaration: declarationData.orgDeclaration,
//                 declNameOrg: declarationData.orgName,
//                 adverseDeclaration: declarationData.adverseDeclaration,
//                 declAdverseDetails: declarationData.adverseDetails
//             };

//             await EnclosureService.submitApplication(submissionData.orgId, submissionData.appId, submissionData);

//             showNotification('Application submitted successfully', 'success');
//             setSubmitDialog(false);

//             // Update application stage
//             setapplicationStage('3a');

//         } catch (error) {
//             console.error('Error submitting application:', error);
//             showNotification('Error submitting application. Please try again.', 'error');
//         }
//     };

//     const handleSendToLA = async () => {
//         try {
//             if (!password) {
//                 showNotification('Please enter your password', 'error');
//                 return;
//             }

//             await EnclosureService.sendToCAB(getAppId(), password);

//             showNotification('Application sent to CAB successfully', 'success');
//             setSendToLADialog(false);
//             setPassword('');

//             // Navigate to CAB section
//             // TODO: Implement navigation
//             // window.location.href = '/cabNew/default';

//         } catch (error) {
//             console.error('Error sending to LA:', error);
//             showNotification('Invalid password or error occurred', 'error');
//         }
//     };

//     // Get completion statistics
//     const getCompletionStats = () => {
//         const total = checklistData.length;
//         const completed = checklistData.filter(item => item.status === 'completed').length;
//         const partial = checklistData.filter(item => item.status === 'partial').length;
//         const pending = checklistData.filter(item => item.status === 'not-filled').length;
//         const notApplicable = checklistData.filter(item => item.status === 'not-applicable').length;

//         return { total, completed, partial, pending, notApplicable };
//     };

//     // Refresh data function
//     const handleRefresh = () => {
//         loadAdequacyCheckData();
//     };

//     return (
//         <Box sx={{ p: 3 }}>
//             <Paper elevation={3} sx={{ p: 3 }}>
//                 {/* File Upload Section */}
//                 <Box sx={{ mt: 4 }}>

//                     <Box sx={{ maxWidth: 600 }}>
//                         <FileUploadComponent
//                             fieldName="proficiencyTestingDocument"
//                             label="Proficiency Testing Document"
//                             orgId={getOrgId()}
//                             section="enclosure"
//                             required={false}
//                             accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.xls"
//                             maxSize={15}
//                             onChange={handleFileUpload}
//                             initialFile={uploadedFile}
//                             localDeleteOnly={true}
//                         />
//                     </Box>
//                     {(applicationStage === null || applicationStage === '18a') && (
//                         <>
//                             {/* Info Alert */}
//                             <Alert severity="info" sx={{ mt: 2, maxWidth: 600 }}>
//                                 <Typography variant="body2">
//                                     After uploading, click "Save File" to permanently save the file reference to your application.
//                                 </Typography>
//                             </Alert>

//                             {/* Save to Database Button - Show only when applicationStage is null or '18a' */}

//                             <Box sx={{ mt: 2, maxWidth: 600 }}>
//                                 <Button
//                                     variant="contained"
//                                     onClick={handleSaveToDatabase}
//                                     disabled={!uploadedFile || saving}
//                                     sx={{
//                                         background: uploadedFile && !saving
//                                             ? 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)'
//                                             : undefined,
//                                         color: 'white',
//                                         boxShadow: uploadedFile && !saving
//                                             ? '0 3px 5px 2px rgba(33, 203, 243, .3)'
//                                             : undefined,
//                                         '&:hover': {
//                                             background: uploadedFile && !saving
//                                                 ? 'linear-gradient(45deg, #1976D2 30%, #00BCD4 90%)'
//                                                 : undefined,
//                                         },
//                                         '&:disabled': {
//                                             background: '#cccccc',
//                                             color: '#666666'
//                                         }
//                                     }}
//                                     startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
//                                 >
//                                     {saving ? 'Saving...' : 'Save File'}
//                                 </Button>
//                             </Box>
//                         </>
//                     )}

//                 </Box>

//                 {(applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") && (
//                     <Box sx={{ mt: 4, mb: 4 }}>
//                         <AdequacyCheckSection
//                             sectionId="enclosure"
//                             title="Proficiency Testing Document"
//                             applicationStage={applicationStage}
//                             orgId={getOrgId()}
//                             appId={getAppId()}
//                             adequacyChecks={adequacyChecks}
//                             handleAdequacyCheck={handleAdequacyCheck}
//                             handleAdequacyRemarks={handleAdequacyRemarks}
//                             handleAdequacySubmit={handleAdequacySubmit}
//                             loading={adequacyLoading}
//                             currentUser={localStorage.getItem('userName') || 'System User'}
//                             currentUserId={parseInt(localStorage.getItem('userId') || '0')}
//                         />
//                     </Box>
//                 )}
//                 {capacity == 0 && (

//                     <>
//                         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
//                             <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
//                                 Application Form - Check List
//                             </Typography>
//                             <IconButton
//                                 onClick={handleRefresh}
//                                 disabled={loading}
//                                 color="primary"
//                                 sx={{
//                                     bgcolor: 'primary.light',
//                                     color: 'white',
//                                     '&:hover': { bgcolor: 'primary.main' }
//                                 }}
//                             >
//                                 <RefreshIcon />
//                             </IconButton>
//                         </Box>

//                         {/* Error State */}
//                         {
//                             error && (
//                                 <Alert
//                                     severity="error"
//                                     sx={{ mb: 3 }}
//                                     action={
//                                         <IconButton
//                                             color="inherit"
//                                             size="small"
//                                             onClick={() => setError(null)}
//                                         >
//                                             <CloseIcon fontSize="inherit" />
//                                         </IconButton>
//                                     }
//                                 >
//                                     {error}
//                                 </Alert>
//                             )
//                         }

//                         {/* Loading State */}
//                         {
//                             loading && (
//                                 <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
//                                     <LinearProgress sx={{ width: '50%' }} />
//                                     <Typography variant="body2" sx={{ ml: 2 }}>
//                                         Loading checklist data...
//                                     </Typography>
//                                 </Box>
//                             )
//                         }


//                         {/* Checklist Table */}
//                         {
//                             !loading && checklistData.length > 0 && (
//                                 <TableContainer component={Paper} variant="outlined">
//                                     <Table sx={{ minWidth: 800 }}>
//                                         <TableHead>
//                                             <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
//                                                 <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '10%' }}>
//                                                     S No.
//                                                 </TableCell>
//                                                 <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '60%' }}>
//                                                     Information / Details
//                                                 </TableCell>
//                                                 <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '15%', textAlign: 'center' }}>
//                                                     Status
//                                                 </TableCell>
//                                                 <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '15%', textAlign: 'center' }}>
//                                                     Availability
//                                                 </TableCell>
//                                             </TableRow>
//                                         </TableHead>
//                                         <TableBody>
//                                             {checklistData.map((item) => (
//                                                 <TableRow
//                                                     key={item.id}
//                                                     sx={{
//                                                         '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
//                                                         '&:hover': { backgroundColor: 'action.selected' }
//                                                     }}
//                                                 >
//                                                     <TableCell sx={{ fontWeight: 'medium' }}>
//                                                         {item.sno}
//                                                     </TableCell>
//                                                     <TableCell>
//                                                         <Tooltip title={item.tooltip} placement="top" arrow>
//                                                             <Typography variant="body2" sx={{ lineHeight: 1.4, cursor: 'help' }}>
//                                                                 {item.information}
//                                                             </Typography>
//                                                         </Tooltip>
//                                                         {/* Show remarks from API if available for incomplete items */}
//                                                         {item.remarks && item.chec === '~/assets/pngegg 1.svg' && (
//                                                             <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
//                                                                 {item.remarks.replace(/<br \/>/g, ' ')}
//                                                             </Typography>
//                                                         )}
//                                                     </TableCell>
//                                                     <TableCell sx={{ textAlign: 'center' }}>
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//                                                             {/* Show tick/cross icons based on chec field */}
//                                                             {item.chec === '~/assets/pngegg 2.svg' ? (
//                                                                 // Show tick icon for completed (pngegg 2)
//                                                                 <CheckIcon fontSize="medium" sx={{ color: 'success.main' }} />
//                                                             ) : (
//                                                                 // Show cross icon for incomplete (pngegg 1)
//                                                                 <CloseIcon fontSize="medium" sx={{ color: 'error.main' }} />
//                                                             )}
//                                                         </Box>
//                                                     </TableCell>
//                                                     <TableCell sx={{ textAlign: 'center' }}>
//                                                         <Chip
//                                                             label={getStatusText(item.status)}
//                                                             color={getStatusColor(item.status)}
//                                                             size="small"
//                                                             variant="filled"
//                                                             sx={{
//                                                                 fontWeight: 'bold',
//                                                                 minWidth: 80
//                                                             }}
//                                                         />
//                                                     </TableCell>
//                                                 </TableRow>
//                                             ))}
//                                         </TableBody>
//                                     </Table>
//                                 </TableContainer>
//                             )
//                         }


//                         {/* Declaration Available Notice */}
//                         {
//                             showDeclaration && (
//                                 <Alert severity="success" sx={{ mt: 3 }} icon={<CheckCircleIcon />}>
//                                     <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
//                                         🎉 All Enclosure Requirements Completed!
//                                     </Typography>
//                                     <Typography variant="body2">
//                                         The Declaration section is now available in the sidebar menu. Please proceed to complete your NABL accreditation declaration.
//                                     </Typography>
//                                 </Alert>
//                             )
//                         }
//                     </>

//                 )}


//             </Paper >

//             {/* Submit Application Dialog */}
//             < Dialog open={submitDialog} onClose={() => setSubmitDialog(false)} maxWidth="sm" fullWidth >
//                 <DialogTitle>Submit Application</DialogTitle>
//                 <DialogContent>
//                     <Typography variant="body1" sx={{ mb: 2 }}>
//                         Are you sure you want to submit the application? This action cannot be undone.
//                     </Typography>
//                     <Typography variant="body2" color="text.secondary">
//                         Please ensure all required documents are uploaded and information is correct.
//                     </Typography>
//                 </DialogContent>
//                 <DialogActions>
//                     <Button onClick={() => setSubmitDialog(false)}>Cancel</Button>
//                     <Button onClick={handleSubmitApplication} variant="contained" color="success">
//                         Submit Application
//                     </Button>
//                 </DialogActions>
//             </Dialog >

//             {/* Send to LA Dialog */}
//             < Dialog open={sendToLADialog} onClose={() => setSendToLADialog(false)} maxWidth="sm" fullWidth >
//                 <DialogTitle>Send to CAB</DialogTitle>
//                 <DialogContent>
//                     <Typography variant="body1" sx={{ mb: 2 }}>
//                         Enter your password to send the application to CAB.
//                     </Typography>
//                     <TextField
//                         label="Password"
//                         type="password"
//                         value={password}
//                         onChange={(e) => setPassword(e.target.value)}
//                         variant="outlined"
//                         fullWidth
//                         required
//                         sx={{ mt: 1 }}
//                     />
//                 </DialogContent>
//                 <DialogActions>
//                     <Button onClick={() => setSendToLADialog(false)}>Cancel</Button>
//                     <Button onClick={handleSendToLA} variant="contained" color="warning" disabled={!password}>
//                         Send to CAB
//                     </Button>
//                 </DialogActions>
//             </Dialog >

//             {/* Notification Snackbar */}
//             < Snackbar
//                 open={notification.open}
//                 autoHideDuration={6000}
//                 onClose={closeNotification}
//                 anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
//             >
//                 <Alert
//                     onClose={closeNotification}
//                     severity={notification.severity}
//                     sx={{ width: '100%' }}
//                 >
//                     {notification.message}
//                 </Alert>
//             </Snackbar >
//         </Box >
//     );
// };

// export default EnclosureSection;